import { Directive, EventEmitter, Inject, OnInit, Output, OnDestroy, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { first, firstValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import { UploadReturn } from 'src/app/core/services/base/document.service';
import { ClaimDocumentService } from 'src/app/core/services/claim_document.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { PersonalDocumentService } from 'src/app/core/services/personal_document.service';
import { UserService } from 'src/app/core/services/user.service';
import { KycDataService } from 'src/app/modules/client/pages/guide/services/kyc-data.service';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { ActionPollingService, PollingResult, PollingExhaustedError } from 'src/app/modules/client/pages/guide/services/action-polling.service';
import {
  DocumentTypeEnum,
  KycCheckBody,
  ProcessingStatus,
} from './document-kyc-upload-modal.component';

import {
  DEFAULT_POLLING_CONFIG,
  DEFAULT_RETRY_CONFIG,
  FileInfo,
  FileValidationResult,
  KycError,
  KycErrorCode,
  KycStep,
  KycUploadDialogData,
  PollingConfig,
  ProcessingStep,
  RetryConfig,
} from './kyc-upload.types';
import { KycFileValidationService } from './services/kyc-file-validation.service';
import { KycMessageService } from './services/kyc-message.service';
import {
  mapSomosV1StageToSteps,
  SomosV1ProcessingStep,
  SOMOS_V1_ACTION_NEEDED_HINT_KEY,
  SOMOS_V1_STEP_ORDER,
  SOMOS_V1_STEP_LABEL_KEYS,
  SOMOS_V1_STEP_HINT_KEYS,
} from './somos-kyc-steps';
import { getVeriffReasonMessage } from './kyc-error-veriff.types';
import { cpfValidator } from 'src/app/shared/utils/cpf.validator';
import * as _moment from 'moment';
const moment = _moment as any;
import { LegalNameFixModalComponent } from '../legal-name-fix-modal/legal-name-fix-modal.component';
import { PersonalDetailsModalComponent, PersonalDetailsDialogData } from '../personal-details-modal/personal-details-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import {
  SerproNameConfirmModalComponent,
  SerproNameConfirmData,
  SerproNameConfirmResult,
} from '../serpro-name-confirm-modal/serpro-name-confirm-modal.component';
import { AgentReviewConfirmationModalComponent } from '../agent-review-confirmation-modal/agent-review-confirmation-modal.component';
import {
  AgentSkipKycModalComponent,
  AgentSkipKycDialogData,
  AgentSkipKycDialogResult,
} from '../agent-skip-kyc-modal/agent-skip-kyc-modal.component';
import { KycSkipReason } from 'src/app/core/services/kyc.service';
import { uniqueId } from 'lodash';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import {
  notesAllowProceed,
  getPostponeMessage,
  getPostponeBlocks,
  notesRequireReview,
  getReviewMessage,
  getReviewBlocks,
  postponeOffersRetryChoice,
  oaPostponeFooter,
  OaPostponeFooter,
  holdPostponeOnRetryLimit,
} from 'src/app/core/utils/kyc-postpone.util';
import { confirmAdoptSerproName, decideTerminalAfterRevalidate } from 'src/app/core/utils/kyc-name-fix.util';
import { KycTraceService, KycTracePhase, KycTraceLevel } from 'src/app/core/services/kyc-trace.service';
import type { DocumentCheckFn } from 'src/app/full-capture/full-capture';
import { MessageDocument } from 'src/app/core/models/message-blocks';

function dobValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const m = moment.isMoment(control.value) ? control.value : moment(control.value);
  if (!m.isValid() || m.isSameOrAfter(moment(), 'day')) {
    return { invalidDob: true };
  }
  return null;
}

@Directive()
export abstract class BaseKycProviderComponent implements OnInit, OnDestroy {
  @Output() completed = new EventEmitter();

  // ==================== STEPS & STATUS ====================

  steps: KycStep = 'TYPE-DOC';
  // steps: KycStep = 'RETRY_LIMIT_EXCEEDED'; // just for test

  result: any = {};
  processing: ProcessingStatus = ProcessingStatus.PROCESSING;

  // CPK-3620: postpone — KYC FAILED but allowed to proceed (notes.postpone_reason.canProceed).
  // When true, the SUCCESS screen shows an "under review" message (the action's
  // configured postpone message) instead of "Identity Verified!".
  isPostpone: boolean = false;
  // TC6: when the postpone was caused by a document-readability issue (Veriff
  // could not read the document), the SUCCESS screen offers an informed choice —
  // resubmit a clearer document, or refer the check to manual review.
  postponeOffersRetry: boolean = false;
  postponeMessage: string = '';
  // CPK-3802: optional block-based postpone message. When set, the SUCCESS
  // screen renders it via <app-message-renderer> instead of the plain
  // postponeMessage text. Comes from cp_actions.config.postpone.blocks.
  postponeBlocks: MessageDocument | null = null;

  // CPK-4807: footer of the postpone screen — 'retry' / 'close' only when reopened from the
  // outstanding action; the guide and a fresh submission always get 'proceed'.
  get postponeFooter(): OaPostponeFooter {
    return oaPostponeFooter({
      reopenedFromOutstandingAction: this.data?.reopenedFromOutstandingAction,
      isPostpone: this.isPostpone,
      offersRetryChoice: this.postponeOffersRetry,
      retriesLoading: this.isLoadingRetries,
      currentRetries: this.serverCurrentRetries,
      maxRetries: this.serverMaxRetries,
    });
  }

  // REVIEW terminal — a FAILED, non-postponable compliance/watchlist block. Reuses the
  // success-card layout (no action button) and shows per-action copy from the note's
  // `review_reason` (data.review_reason.{messages,blocks}), falling back to the default
  // KycReviewTitle / KycReviewMessage i18n keys when the action configured none.
  reviewMessage: string = '';
  reviewBlocks: MessageDocument | null = null;

  // ==================== DOCUMENT TYPE ====================
  DocumentType = DocumentTypeEnum;
  documentType: DocumentTypeEnum | null = null;
  options = [
    { value: DocumentTypeEnum.PASSPORT, label: 'Passport' },
    {
      value: DocumentTypeEnum.ID_CARD,
      label: 'ID Card',
    },
    { value: DocumentTypeEnum.RESIDENCE_PERMIT, label: 'Residence Permit' },
    { value: DocumentTypeEnum.DRIVER_LICENSE, label: 'Driver License' },
  ];

  // ==================== CNH TYPE (Digital or Physical) ====================
  cnhType: 'digital' | 'physical' | null = null;
  /**
   * The capture is a picture of a digital CNH accepted on its signed QR (see
   * `CaptureFlowResult.digitalPicture`). Rides to the lambda on
   * `providerAdditionalInfo.digitalPicture`, where a Veriff rejection of this check
   * becomes a terminal postpone instead of a resubmission the user cannot satisfy.
   */
  digitalPictureCapture = false;
  /**
   * Our reader-cnh verdict on the captured document's SERPRO signature — `verified`,
   * `invalid`, `unverified`, `unsupported`, or null when no QR was decoded. Rides to the
   * lambda on `providerAdditionalInfo.documentSignature`, where the (config-listed)
   * `document_signature` validation turns "could not validate" into the postponable
   * `document_signature_invalid` token instead of letting it pass unremarked.
   */
  documentSignatureStatus: string | null = null;

  /** True when the digital/physical toggle should be shown and required. */
  get showCnhToggle(): boolean {
    return (
      this.documentType === DocumentTypeEnum.DRIVERS_LICENSE ||
      this.documentType === DocumentTypeEnum.DRIVER_LICENSE
    ) && (this.data?.provider === 'verifflite' || this.data?.provider === 'somoskyc_v1');
  }

  /**
   * True when the document uses the two-sided upload UI (front + back) and
   * therefore requires a back image — e.g. RG / ID card / residence permit.
   * Passport and the single-file CNH flow only need the front image.
   */
  get requiresBackImage(): boolean {
    return (
      this.documentType !== DocumentTypeEnum.PASSPORT && !this.showCnhToggle
    );
  }

  /**
   * True only for the native Brazilian SomosKycV1 provider. Gates every SomosKycV1-only
   * behaviour (guided capture, attempt-trace telemetry, the SERPRO name gate, and the
   * corrected-details resubmission flow) so the other providers — verifflite, veriff,
   * thirdfort, textract — keep their exact pre-SomosKycV1 behaviour.
   */
  get isSomosV1(): boolean {
    return this.data?.provider === 'somoskyc_v1';
  }

  // ==================== BRDOCS GUIDED CAPTURE ====================
  /**
   * When true, the TYPE-DOC + UPLOAD steps are replaced by the brdocs guided
   * capture component (`<brdocs-capture-flow>`). Set by providers that opt in
   * (SomosKycV1). The compiled capture result is handled by
   * {@link onBrdocsCompleted}.
   */
  useBrdocsCapture = false;

  /**
   * Optional per-action override (ms) for how long the guided capture auto-scans before the
   * manual "Tirar foto" button appears. Bound to `<brdocs-capture-flow [manualFallbackMs]>`.
   * Undefined leaves the capture component on its own default; SomosKycV1 resolves it from
   * `cp_actions.config.somosKyc.documentScanTimeSeconds` in its `ngOnInit`.
   */
  manualFallbackMs?: number;

  /**
   * CPK-5103: optional is_document check run by the guided capture on each front/back capture
   * before its preview. Bound to `<brdocs-capture-flow [documentCheck]>`; null = no check.
   * SomosKycV1 sets it in `ngOnInit` when `cp_actions.config.isDocumentCheck.enabled` is true.
   */
  documentCheck: DocumentCheckFn | null = null;

  /** Handle to the embedded brdocs flow (to restart it on "use another document"). */
  @ViewChild('brdocsFlow') brdocsFlow?: any;

  /** Full timestamped trace of this KYC attempt (capture → pre-check → submit → result). */
  protected trace = inject(KycTraceService);
  /** Seq of the last trace event already streamed to the DB. */
  private traceSentSeq = 0;
  /** Periodic flusher handle — streams new trace events to the DB as the attempt unfolds. */
  private traceTimer: any = null;
  /** Last polled status, so the poll trace can flag stage transitions rather than repeat ticks. */
  private lastPollStatus: string | null = null;

  /** Begin streaming the trace to the DB in near-real-time (a batch ~every second). */
  protected startTraceStream(): void {
    // Attempt-trace telemetry is a SomosKycV1 feature only — never emit it for other providers.
    if (!this.isSomosV1) return;
    this.stopTraceStream();
    this.traceSentSeq = 0;
    this.traceTimer = setInterval(() => this.flushTraceDelta(), 1000);
  }

  protected stopTraceStream(): void {
    if (this.traceTimer) {
      clearInterval(this.traceTimer);
      this.traceTimer = null;
    }
  }

  /**
   * Stream the trace events logged since the last flush as one `KYC_ATTEMPT_TRACE` analytics event
   * (cp_event) — so the DB reflects the attempt as it happens, and a crash/abandon mid-flow still
   * leaves everything-so-far persisted. Append-only batches correlate via `attempt_id`; the
   * `kyc_check_id` is attached once the check exists. Fire-and-forget; never blocks the flow.
   */
  protected flushTraceDelta(): void {
    // SomosKycV1-only telemetry — no attempt-trace events for verifflite/veriff/thirdfort/textract.
    if (!this.isSomosV1) return;
    const batch = this.trace.delta(this.traceSentSeq);
    if (!batch.length) return;
    this.traceSentSeq = batch[batch.length - 1].seq;
    this.eventService
      .sendEventWithCaseId(EventTypes.KYC_ATTEMPT_TRACE, this.data.claim_id, {
        attempt_id: this.trace.attemptId,
        kyc_check_id: this.checkId || this.actionDoneActionId || null,
        attempt_label: this.trace.attemptLabel,
        batch_from_seq: batch[0].seq,
        batch_to_seq: batch[batch.length - 1].seq,
        events: batch,
      })
      .catch(() => {
        /* events must never block the flow */
      });
  }

  /**
   * One-time audit context for the attempt: who is running it (actor vs. the subject being
   * verified, agent-impersonation flag), on what device/browser, and in which session/locale.
   * The server stamps the source IP + receive time onto each trace batch (see event-push-to-queue);
   * this fills in everything the browser knows that the raw user-agent string doesn't. Best-effort.
   */
  private async logAttemptContext(): Promise<void> {
    // SomosKycV1-only — skip the extra getCurrentUser() lookup + audit event for other providers.
    if (!this.isSomosV1) return;
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    const scr: any = typeof screen !== 'undefined' ? screen : {};

    let actorId: string | null = null;
    let actorName: string | null = null;
    try {
      const u = await this.us.getCurrentUser();
      actorId = u?.id ?? null;
      actorName = u?.display_name ?? null;
    } catch {
      /* best-effort — never block the attempt */
    }

    let timezone: string | null = null;
    try {
      timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone ?? null;
    } catch {
      /* ignore */
    }

    this.trace.log('system', 'info', 'Attempt context (device · session · actor)', {
      provider: this.data?.provider ?? null,
      action_id: this.data?.action_id ?? null,
      claim_id: this.data?.claim_id ?? null,
      case_id: (this.data as any)?.case_id ?? null,
      subject_user_id: this.data?.ref ?? null,
      actor_user_id: actorId,
      actor_name: actorName,
      actor_is_subject: actorId != null && actorId === (this.data?.ref ?? null),
      agent_session: this.isAgentSession,
      user_agent: nav.userAgent ?? null,
      platform: nav.platform ?? null,
      languages: Array.isArray(nav.languages) ? nav.languages.join(',') : nav.language ?? null,
      timezone,
      screen: scr.width ? `${scr.width}×${scr.height}` : null,
      viewport: typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : null,
      device_pixel_ratio: typeof window !== 'undefined' ? window.devicePixelRatio ?? null : null,
      device_memory_gb: nav.deviceMemory ?? null,
      cpu_cores: nav.hardwareConcurrency ?? null,
      connection: nav.connection?.effectiveType ?? null,
      online: nav.onLine ?? null,
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      url: typeof location !== 'undefined' ? location.href : null,
    });
  }

  /** Overridden by providers that use the brdocs capture flow. */
  onBrdocsCompleted(_result: any): void {}

  /** The guided flow acquired the camera. Overridden by providers that emit analytics. */
  onBrdocsCameraStarted(_info: any): void {}

  /**
   * Low-level brdocs diagnostics (camera lifecycle, OCR legibility, classification scores, QR /
   * signature reads, model loading, errors). Mirrored to the console and to the KYC attempt trace.
   */
  onBrdocsDebug(e: { level: 'info' | 'warn' | 'error'; category: string; message: string; data?: Record<string, unknown> }): void {
    const fn = e.level === 'error' ? console.error : e.level === 'warn' ? console.warn : console.log;
    fn(`[brdocs:${e.category}] ${e.message}`, e.data ?? '');
    this.trace.log(BRDOCS_CATEGORY_PHASE[e.category] ?? 'capture', e.level, e.message, e.data ?? null);
  }

  /**
   * User-action stream from the brdocs flow (method chosen, front/back captured, QR read/skipped,
   * retakes, …). Recorded to the trace so the timeline shows what the user did at each step —
   * e.g. "photo vs upload", which document the classifier recognised, whether the QR was read.
   */
  onBrdocsAction(e: { action: string; step?: string; method?: string; detail?: Record<string, unknown> }): void {
    const title = BRDOCS_ACTION_TITLE(e.action, e.method, e.detail);
    this.trace.log(BRDOCS_ACTION_PHASE[e.action] ?? 'capture', 'info', title, {
      action: e.action,
      step: e.step,
      method: e.method,
      ...(e.detail ?? {}),
    });
  }

  // ==================== FILE HANDLING ====================
  frontImageFile: File | null = null;
  backImageFile: File | null = null;
  frontFileMetadata: any = null;
  backFileMetadata: any = null;
  frontImage: string | null = '';
  backImage: string | null = '';

  // NEW: File validation and preview
  frontFileInfo: FileInfo | null = null;
  backFileInfo: FileInfo | null = null;
  frontValidation: FileValidationResult | null = null;
  backValidation: FileValidationResult | null = null;

  // ==================== PROCESSING STATE ====================
  kyc?: {
    provider: string;
    front_id: string;
    back_id?: string;
  };

  checkId: string = '';
  /** Checks whose name-fix popup has already been shown (once per check, confirmed or not). */
  private nameFixPrompted = new Set<string>();
  progress: number = 0;

  // NEW: Enhanced processing state
  currentProcessingStep: ProcessingStep = 'UPLOADING';
  processingSteps: Array<{
    step: ProcessingStep;
    message: string;
    done: boolean;
    hasError?: boolean;
    // SomosKycV1 only — both stay undefined for every other provider, so their rendering is
    // byte-identical. `descriptionKey` is a muted sub-line under the active step; `actionNeeded`
    // flags a step paused waiting on the user (name gate / CPF correction).
    descriptionKey?: string;
    actionNeeded?: boolean;
  }> = [];
  isProcessingIndeterminate: boolean = true;

  // NEW: Error handling
  currentError: KycError | null = null;
  uploadAttempts: number = 0;

  // Server-side retry tracking
  serverCurrentRetries: number = 0;
  serverMaxRetries: number = 0;
  userRole: string = 'CLIENT';
  isLoadingRetries: boolean = true;

  // NEW: Wait timer for shouldWait errors
  waitTimeRemaining: number = 0;
  waitTimer?: any;

  // Elapsed seconds in PROCESSING step — drives the "taking longer" hint
  processingElapsedSeconds: number = 0;
  private processingElapsedTimer?: any;

  // NEW: Processing error state
  hasProcessingError: boolean = false;
  errorInStep: ProcessingStep | null = null;

  // NEW: Cached caseId to avoid multiple lookups
  cachedCaseId: string | null = null;

  // NEW: Configurations
  protected pollingConfig: PollingConfig = DEFAULT_POLLING_CONFIG;
  protected retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG;

  // NEW: Action polling
  protected pollingSubscription?: Subscription;
  protected actionDoneActionId?: string;

  // NEW: Case email for support
  caseEmail: string | null = null;

  // Names mismatch classification (for B2b/B3b error messages)
  private storedMismatchCategory?: 'A' | 'B1' | 'B2' | 'B3' | 'C';
  private storedMismatchSubCategory?: 'a' | 'b' | 'c';

  // Destroy subject for subscription cleanup
  private destroy$ = new Subject<void>();

  // Agent session diagnostics
  isAgentSession: boolean = false;
  agentDiagnostics: {
    errorCode?: string;
    reasonCode?: string;
    provider?: string;
    retries?: string;
    pepSanctions?: boolean | null; // null = not checked (e.g. registry declined before PEP step)
    notesCount?: number;
    rawNotes?: string;
  } | null = null;
  agentActionLoading: 'retries' | 'bypass' | null = null;

  // ==================== CPF CORRECTION FORM ====================
  cpfCorrectionForm: FormGroup | null = null;
  isCpfCorrectionSubmitting: boolean = false;
  originalCpf: string | null = null;
  private originalDob: string | null = null;
  private originalName: string | null = null;
  // Editable corrected details (the registry validates CPF + name + DOB, so all three are editable).
  correctedCpf: string = '';
  correctedName: string = '';
  // Already-translated message shown inline when the correction submit fails (e.g. profile 409).
  cpfCorrectionError: string | null = null;

  // DOB carregado do PII para checar menoridade no RETRY_LIMIT_EXCEEDED
  clientDob: string | null = null;
  private fb = inject(FormBuilder);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: KycUploadDialogData,
    public dialog: MatDialog,
    protected pds: PersonalDocumentService,
    protected cds: ClaimDocumentService,
    protected ks: KycService,
    protected us: UserService,
    protected dialogRef: MatDialogRef<any>,
    protected kds: KycDataService,
    protected validationService: KycFileValidationService,
    public messageService: KycMessageService,
    protected translate: TranslateService,
    protected claimsService: ClaimsService,
    protected actionPollingService: ActionPollingService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
  ) { }

  private eventService = inject(EventService);
  private agentClientsService = inject(AgentClientsService);

  ngOnInit(): void {
    // Start a fresh trace for this attempt so the whole flow (capture → pre-check → submit →
    // result) is captured on a timeline the /kyc-test harness can inspect, and stream it to the DB
    // in near-real-time so the persisted record reflects the attempt as it happens.
    this.trace.start(`${this.data?.provider ?? 'verifflite'}${this.data?.action_id ? ' · action ' + this.data.action_id.slice(0, 8) : ''}`);
    this.startTraceStream();

    // Detect agent impersonation session from JWT
    this.isAgentSession = this.agentClientsService.isAgentSession();

    // Record who/what/where this attempt runs on (device · session · actor) as an audit event.
    void this.logAttemptContext();

    this.result = {
      status: 'CANCELLED',
      value: null,
    };

    if (!this.data.isComponentView) {
      if (this.useBrdocsCapture) {
        // Somos guided-capture flow ONLY: confirm-to-close so in-flight capture isn't silently
        // lost. Setting `disableClose` stops MatDialog from auto-dismissing on backdrop/ESC and
        // routes those gestures through the confirm guard. Terminal steps + the explicit close
        // buttons still close immediately (see `confirmClose`), so the user is never trapped.
        this.dialogRef.disableClose = true;

        // Backdrop click → guarded close. Not `first()`: if the user cancels the confirm they
        // must still be able to click the backdrop again.
        this.dialogRef
          .backdropClick()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.confirmClose());

        // ESC key → guarded close. With `disableClose: true` MatDialog no longer auto-closes on
        // ESC, so we intercept the keydown and route it through the same confirm-to-close guard.
        this.dialogRef
          .keydownEvents()
          .pipe(takeUntil(this.destroy$))
          .subscribe((event) => {
            if (event.key === 'Escape') this.confirmClose();
          });
      } else {
        // Every other provider (Veriff / VeriffLite / Textract / Thirdfort): unchanged original
        // immediate-close-on-backdrop behaviour, restored verbatim.
        this.dialogRef
          .backdropClick()
          .pipe(
            first(),
            takeUntil(this.destroy$)
          )
          .subscribe(() => this.close());
      }
    }

    if (!this.data.provider) this.data.provider = 'verifflite';
    this.kyc = {
      provider: this.data.provider,
      front_id: '',
    };

    this.initializeProcessingSteps();

    // Se vier um erro de Veriff Lite em notes, já prepara o card de erro
    this.checkAndSetVeriffLiteError();

    // Buscar retry count do usuário proativamente
    this.isLoadingRetries = true;
    this.loadUserRetryCount().finally(() => {
      this.isLoadingRetries = false;
    });

    // Fetch case email if claim_id is present
    if (this.data.claim_id) {
      this.fetchCaseEmail();
      this.fetchCaseId();
    }
  }


  private async fetchCaseId(): Promise<void> {
    if (!this.data.claim_id || this.cachedCaseId) {
      return;
    }

    try {
      const claimWithCase: any = await this.claimsService.getClaimWithCase(this.data.claim_id);
      if (claimWithCase?.case_id) {
        this.cachedCaseId = claimWithCase.case_id;
      } else if (claimWithCase?.cp_case?.id) {
        this.cachedCaseId = claimWithCase.cp_case.id;
      }
    } catch (error) {
      console.error('[KYC] Error fetching caseId:', error);
    }
  }
  /**
   * Verifica se há erro de Veriff Lite em notes ou provider_return e prepara o card de erro
   */
  private checkAndSetVeriffLiteError(): void {
    // Só processa se não houver erro já setado
    if (this.currentError) return;

    // Tenta processar erro do Veriff Lite (notes ou provider_return)
    const notes = this.data?.notes;
    console.log('[KYC] Checking for Veriff Lite errors in notes and provider_return', notes);
    const providerReturn = this.data?.provider_return;

    // Terminal-screen precedence — MUST match the polling path (handlePollingSuccess /
    // handlePollingFailure). checkPepSanctionsError must NOT pre-empt a passed or postponable check:
    //   1. passed === true            → VERIFIED success (Identity Verified)
    //   2. notesAllowProceed(notes)   → PROCEED-WITH-CAVEAT (under review, single Proceed)
    //   3. watchlist / review_reason  → REVIEW terminal (manual review, no button)
    if (this.data?.passed === true) {
      console.log('[KYC] checkAndSetVeriffLiteError - passed check on reopen → VERIFIED');
      this.handlePollingSuccess(notes, providerReturn, true);
      return;
    }
    if (notesAllowProceed(notes)) {
      console.log('[KYC] checkAndSetVeriffLiteError - postponable check on reopen → PROCEED-WITH-CAVEAT');
      this.handlePollingSuccess(notes, providerReturn, false);
      return;
    }
    const pepError = this.checkPepSanctionsError(notes) || notesRequireReview(notes);
    if (pepError) {
      console.log('[KYC] checkAndSetVeriffLiteError - compliance/watchlist block → REVIEW terminal');
      this.handleReviewTerminal(notes, providerReturn);
      return; // Não processa outros erros
    }

    const veriffError = this.processVeriffLiteError(notes, providerReturn);

    if (veriffError) {
      console.log('[KYC] checkAndSetVeriffLiteError - Error found in notes:', veriffError);

      // Verificar se precisa abrir modal de legal name fix
      if (veriffError.requiresModal && veriffError.validationType === 'legal_name_match') {
        console.log('[KYC] checkAndSetVeriffLiteError - Opening legal name fix modal');

        // Setar estado de erro antes de abrir modal
        this.currentError = veriffError;
        this.hasProcessingError = true;
        this.errorInStep = this.currentProcessingStep;
        this.processing = ProcessingStatus.REJECTED;
        this.isProcessingIndeterminate = false;
        this.steps = 'PROCESSING'; // Manter em PROCESSING
        this.updateProcessingStep(this.currentProcessingStep, false, true);

        // Abrir modal (veriffError já é garantido não-null aqui)
        this.openLegalNameFixModal(veriffError, providerReturn);
      } else {
        // Outros erros: mostrar tela de erro completa
        this.currentError = veriffError;
        this.hasProcessingError = true;
        this.errorInStep = this.currentProcessingStep;
        this.processing = ProcessingStatus.REJECTED;

        if (veriffError.validationType === 'cpf_validation') {
          // CPF error: show inline in PROCESSING step with correction form
          this.steps = 'PROCESSING';
          // SomosKycV1: present the correctable registry decline as an amber action-needed
          // personal-data step (rather than an unanchored list with nothing spinning).
          this.markSomosPersonalDataActionNeeded();
          this.loadCurrentPiiData();
        } else {
          this.steps = 'ERROR';
        }
      }
    }
  }

  /**
   * Helper para obter o maxRetries efetivo (considerando override da action)
   */
  private async getEffectiveMaxRetries(userId: string): Promise<number> {
    try {
      // 1. Check User Attributes & Role
      const { data: userData } = await this.ks.supabase
        .from('cp_user')
        .select('attributes, primary_role')
        .eq('id', userId)
        .single();

      // Admin/Agent always has Infinity
      if (userData?.primary_role === 'ADMINISTRATOR' || userData?.primary_role === 'AGENT') {
        return Infinity;
      }

      const userAttributes = userData?.attributes as any;
      if (userAttributes?.kyc?.maxRetries !== undefined && userAttributes?.kyc?.maxRetries !== null) {
        console.log('[KYC] Max retries from user attributes:', userAttributes.kyc.maxRetries);
        return Number(userAttributes.kyc.maxRetries);
      }

      // 2. Check Action Config
      if (this.data.action_id) {
        const { data: actionData } = await this.ks.supabase
          .from('cp_actions')
          .select('config')
          .eq('id', this.data.action_id)
          .single();

        const config = actionData?.config as any;
        if (config?.maxRetries !== undefined && config?.maxRetries !== null) {
          console.log('[KYC] Max retries overridden by action config:', config.maxRetries);
          return Number(config.maxRetries);
        }
      }
    } catch (error) {
      console.warn('[KYC] Error calculating effective maxRetries:', error);
    }

    // 3. Default
    return 3;
  }

  /**
   * Carrega o retry count do usuário proativamente
   */
  private async loadUserRetryCount(): Promise<void> {
    try {
      // Pega o user_id do data ou do usuário logado
      let userId = this.data.ref;

      if (!userId) {
        const currentUser = await this.us.getCurrentUser();
        userId = currentUser?.id || '';
      }

      if (!userId) {
        console.warn('[KYC] No user ID available to fetch retry count');
        return;
      }

      const provider = this.data.provider || 'verifflite';

      const { retryCount } = await this.ks.getUserRetryCount(
        userId,
        provider
      );

      const finalMaxRetries = await this.getEffectiveMaxRetries(userId);

      this.serverCurrentRetries = retryCount;
      this.serverMaxRetries = finalMaxRetries;

      console.log('[KYC] Retry count loaded:', {
        currentRetries: retryCount,
        maxRetries: this.serverMaxRetries,
        provider,
      });

      const hasReachedLimit = retryCount >= this.serverMaxRetries && this.serverMaxRetries !== Infinity;

      if (
        hasReachedLimit &&
        this.steps === 'SUCCESS' &&
        holdPostponeOnRetryLimit(this.data?.reopenedFromOutstandingAction, this.isPostpone, this.isAgentSession)
      ) {
        // CPK-4807: postpone reaberto pela outstanding action, cliente sem retries — fica na
        // tela de postpone, que troca "Prosseguir" por "Fechar" (postponeFooter === 'close').
        console.log('[KYC] Retry limit reached on a postpone reopened from the outstanding action — keeping the postpone screen');
      } else if (hasReachedLimit) {
        // Atingiu o limite: forçar step RETRY_LIMIT_EXCEEDED
        this.currentError = {
          code: 'RETRY_LIMIT_EXCEEDED',
          message: `You have reached the maximum number of verification attempts (${retryCount}/${this.serverMaxRetries}).`,
          suggestedAction:
            'Please contact support for assistance with your verification.',
          canRetry: false,
          currentRetries: retryCount,
          maxRetries: this.serverMaxRetries,
        };
        this.steps = 'RETRY_LIMIT_EXCEEDED';
        this.parseAgentDiagnostics(this.data?.notes);
        this.loadClientDob();
      } else if (this.steps === 'RETRY_LIMIT_EXCEEDED' || this.steps === 'BLOCKED' || this.steps === 'REVIEW') {
        // O limite NÃO foi mais atingido (ex: agent incrementou maxRetries),
        // mas um step anterior (checkAndSetVeriffLiteError) setou BLOCKED/RETRY_LIMIT_EXCEEDED
        // com dados antigos. Resetar para permitir nova tentativa.
        console.log('[KYC] Retry limit no longer exceeded after refresh, resetting to UPLOAD step');
        this.currentError = undefined as any;
        this.steps = 'UPLOAD';
      }
    } catch (error) {
      console.error('[KYC] Error loading retry count:', error);
      // Continuar normalmente em caso de erro
    }
  }

  ngOnDestroy(): void {
    // Flush any trailing events (e.g. the user closed the modal mid-flow) and stop the streamer.
    this.flushTraceDelta();
    this.stopTraceStream();

    // Emit destroy signal to complete all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Limpa timer se existir
    if (this.waitTimer) {
      clearInterval(this.waitTimer);
    }

    this.stopProcessingElapsedTimer();
  }

  private startProcessingElapsedTimer(): void {
    this.stopProcessingElapsedTimer();
    this.processingElapsedSeconds = 0;
    this.processingElapsedTimer = setInterval(() => {
      this.processingElapsedSeconds++;
    }, 1000);
  }

  private stopProcessingElapsedTimer(): void {
    if (this.processingElapsedTimer) {
      clearInterval(this.processingElapsedTimer);
      this.processingElapsedTimer = undefined;
    }
    this.processingElapsedSeconds = 0;
  }

  // ==================== FILE HANDLING METHODS ====================

  async changeImage(event: File | null, side: 'front' | 'back') {
    if (!event) {
      this.clearFile(side);
      return;
    }

    // Validate file
    const validation = await this.validationService.validateFile(event);

    if (side === 'front') {
      this.frontValidation = validation;
      if (!validation.valid) {
        this.currentError = this.messageService.getErrorDetails(
          (validation.errors?.[0]?.rule as KycErrorCode) ?? 'FILE_INVALID_TYPE',
          validation.errors?.[0]?.message
        );
        return;
      }
    } else {
      this.backValidation = validation;
      if (!validation.valid) {
        this.currentError = this.messageService.getErrorDetails(
          (validation.errors?.[0]?.rule as KycErrorCode) ?? 'FILE_INVALID_TYPE',
          validation.errors?.[0]?.message
        );
        return;
      }
    }

    // Get file info with preview
    const fileInfo = await this.validationService.getFileInfo(event);

    if (side === 'front') {
      this.frontImage = fileInfo.preview || '';
      this.frontImageFile = event;
      this.frontFileInfo = fileInfo;
    } else {
      this.backImage = fileInfo.preview || '';
      this.backImageFile = event;
      this.backFileInfo = fileInfo;
    }

    // Clear error if validation passed
    if (this.currentError) {
      this.currentError = null;
    }
  }

  private clearFile(side: 'front' | 'back') {
    if (side === 'front') {
      this.frontImage = null;
      this.frontImageFile = null;
      this.frontFileInfo = null;
      this.frontValidation = null;
    } else {
      this.backImage = null;
      this.backImageFile = null;
      this.backFileInfo = null;
      this.backValidation = null;
    }
  }

  private clearAllFiles() {
    this.clearFile('front');
    this.clearFile('back');
  }

  // ==================== STEP NAVIGATION ====================

  protected processDocumentType() {
    console.log('[KYC] Document type selected:', {
      documentType: this.documentType,
      provider: this.data.provider,
      options: this.options,
    });
    this.frontImage = '';
    this.backImage = '';
    this.cnhType = null;
    this.digitalPictureCapture = false;
    this.documentSignatureStatus = null;
    this.steps = 'UPLOAD';

    if (this.showCnhToggle && this.cnhType) {
      this.trackCnhTypeSelection(this.cnhType);
    }
  }

  protected async processUpload() {
    // Get client name
    const { display_name } = (await this.us.getCurrentUser())!;
    const [firstName, ...rest] = display_name!.split(' ');
    const restName = rest.join(' ');

    // Validate files before upload
    const hasValidFiles = this.validateFilesForUpload();
    if (!hasValidFiles) {
      return;
    }

    this.uploadAndProcess();
  }

  protected async processError(): Promise<void> {
    this.stopProcessingElapsedTimer();
    // Send KYC_TRY_AGAIN event
    if (this.data.claim_id) {
      this.eventService.sendEventWithCaseId(
        EventTypes.KYC_TRY_AGAIN,
        this.data.claim_id
      ).catch(error => {
        console.error('[KYC] Error sending KYC_TRY_AGAIN event:', error);
      });
    }

    // Before resetting: check if an agent has already manually approved this KYC.
    // This handles the case where the client is stuck in the error dialog and the
    // agent adds a manual pass while the dialog is open.
    if (this.data.ref && this.data.action_id) {
      const passedCheck = await this.ks.getPassedCheckForUser(this.data.ref, this.data.action_id);
      if (passedCheck) {
        this.dialogRef.close({ status: 'APPROVED', kycCheckId: passedCheck.id });
        return;
      }
    }

    // Reset for retry
    this.uploadAttempts = 0;
    this.currentError = null;
    this.hasProcessingError = false;
    this.errorInStep = null;
    this.clearAllFiles();

    // Reset processing state
    this.currentProcessingStep = 'UPLOADING';
    this.isProcessingIndeterminate = true;
    this.processing = ProcessingStatus.PROCESSING;

    // Reset processing steps
    this.initializeProcessingSteps();

    // Recarregar retry count
    this.loadUserRetryCount();

    this.steps = 'UPLOAD';
  }

  // ==================== CPF CORRECTION METHODS ====================

  /**
   * Loads current PII data (CPF and DOB) and initializes the correction form
   */
  protected async loadCurrentPiiData(): Promise<void> {
    try {
      let userId = this.data.ref;
      if (!userId) {
        const currentUser = await this.us.getCurrentUser();
        userId = currentUser?.id || '';
      }
      if (!userId) return;

      // SomosKycV1 also needs legal_name for its editable name field; other providers keep the
      // pre-SomosKycV1 query (CPF + DOB only) exactly as before.
      const { data } = await this.ks.supabase
        .from('cp_pii')
        .select(this.isSomosV1 ? 'id_document_value, date_of_birth, legal_name' : 'id_document_value, date_of_birth')
        .eq('user_id', userId)
        .maybeSingle();

      const rawCpf = (data as any)?.id_document_value || '';
      const rawDob = (data as any)?.date_of_birth || '';

      // Format CPF for display: 00000000000 -> 000.000.000-00
      const displayCpf = rawCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');

      // DOB as moment object (input-date expects moment)
      const momentDob = rawDob ? moment(rawDob, 'YYYY-MM-DD') : null;

      this.originalCpf = displayCpf;
      this.originalDob = rawDob; // store as ISO for comparison
      this.originalName = (data as any)?.legal_name || '';
      this.clientDob = rawDob;
      this.cpfCorrectionError = null;

      // SomosKycV1 only: prefill the editable correction fields with the current values.
      if (this.isSomosV1) {
        this.correctedCpf = displayCpf;
        this.correctedName = this.originalName ?? '';
      }

      this.cpfCorrectionForm = this.fb.group({
        dob: [momentDob, [Validators.required, dobValidator]],
      });
    } catch (error) {
      console.error('[KYC] Error loading PII data for CPF correction:', error);
      this.cpfCorrectionForm = this.fb.group({
        cpf: ['', [Validators.required, cpfValidator]],
        dob: ['', [Validators.required, dobValidator]],
      });
    }
  }

  /**
   * Loads only the client's DOB from PII — used to check underage status
   * at the RETRY_LIMIT_EXCEEDED step (where cpfCorrectionForm is not available)
   */
  private async loadClientDob(): Promise<void> {
    try {
      let userId = this.data.ref;
      if (!userId) {
        const currentUser = await this.us.getCurrentUser();
        userId = currentUser?.id || '';
      }
      if (!userId) return;

      const { data } = await this.ks.supabase
        .from('cp_pii')
        .select('date_of_birth')
        .eq('user_id', userId)
        .maybeSingle();

      this.clientDob = data?.date_of_birth || null;
    } catch (error) {
      console.error('[KYC] Error loading client DOB:', error);
    }
  }

  /**
   * Returns true if the client's DOB indicates an underage client (< 18 years)
   */
  get isUnderageClient(): boolean {
    // return true; // TODO: remover antes do commit
    const dob = this.clientDob
      ? moment(this.clientDob, 'YYYY-MM-DD')
      : null;
    return !!dob && dob.isValid() && moment().diff(dob, 'years') < 18;
  }

  /**
   * Agent action: increase retries and navigate back to DOB correction form.
   * Used when client may have entered an incorrect date of birth.
   */
  async onAgentFixDob(): Promise<void> {
    const userId = this.data.ref;
    if (!userId || this.agentActionLoading) return;

    this.agentActionLoading = 'retries';
    try {
      const result = await this.ks.agentIncreaseRetries(userId);
      this.serverMaxRetries = result.newMax;
      alert(this.translate.instant('KYC.AGENT_ACTIONS.SUCCESS_RETRIES', result));

      // Navigate back to CPF correction form
      await this.loadCurrentPiiData();
      this.currentError = {
        code: 'cpf_validation',
        message: this.translate.instant('KYC.ERROR.UNABLE_VALIDATE_CPF'),
        validationType: 'cpf_validation',
        canRetry: true,
      } as any;
      this.hasProcessingError = true;
      this.steps = 'PROCESSING';
      // SomosKycV1: anchor the reopened correction form on the amber personal-data step.
      this.markSomosPersonalDataActionNeeded();
    } catch (err) {
      console.error('[KYC] Agent fix DOB failed:', err);
      alert(this.translate.instant('KYC.AGENT_ACTIONS.ERROR_GENERIC'));
    } finally {
      this.agentActionLoading = null;
    }
  }

  /**
   * Agent action: bypass KYC for an underage client.
   * Only available when isAgentSession && isUnderageClient.
   * Opens the unified skip modal pre-selecting UNDERAGED so the reason is
   * captured the same way as other skip motives (CPK-3338).
   */
  async onUnderageBypassKyc(): Promise<void> {
    await this.openSkipKycModal('UNDERAGED');
  }

  /**
   * Returns true if either CPF or DOB was changed from the original values
   */
  get isCpfCorrectionChanged(): boolean {
    if (!this.cpfCorrectionForm) return false;
    const dobVal = this.cpfCorrectionForm.get('dob')?.value;
    if (!dobVal && !this.originalDob) return false;
    if (!dobVal || !this.originalDob) return true;
    return !moment(dobVal).isSame(moment(this.originalDob, 'YYYY-MM-DD'), 'day');
  }

  /**
   * Saves corrected CPF/DOB to PII then triggers a retry
   */
  /** True when the corrected CPF (11 digits), name, and DOB are all valid — gates the retry button. */
  get canSubmitCpfCorrection(): boolean {
    const cpfOk = (this.correctedCpf || '').replace(/\D/g, '').length === 11;
    const nameOk = (this.correctedName || '').trim().length > 0;
    const dobOk = !!this.cpfCorrectionForm && this.cpfCorrectionForm.valid;
    return cpfOk && nameOk && dobOk;
  }

  async onCpfCorrectionRetry(): Promise<void> {
    // Non-SomosKycV1 providers keep the pre-SomosKycV1 flow verbatim: read-only CPF, edit DOB
    // only, and persist just id_document_value + date_of_birth.
    if (!this.isSomosV1) {
      if (!this.cpfCorrectionForm || this.cpfCorrectionForm.invalid) return;

      this.isCpfCorrectionSubmitting = true;
      this.cpfCorrectionError = null;
      try {
        const cpfRaw = (this.originalCpf || '').replace(/\D/g, '');
        const dobMoment = this.cpfCorrectionForm.get('dob')!.value;
        const dobIso = moment(dobMoment).format('YYYY-MM-DD');

        await this.us.updateProfile({
          pii: { id_document_value: cpfRaw, date_of_birth: dobIso },
        });

        this.cpfCorrectionForm = null;
        this.originalCpf = null;
        this.originalDob = null;
        this.processError();
      } catch (error) {
        console.error('[KYC] Error updating CPF/DOB:', error);
        this.cpfCorrectionError = this.buildCpfCorrectionErrorMessage(error);
      } finally {
        this.isCpfCorrectionSubmitting = false;
      }
      return;
    }

    // SomosKycV1: CPF + name + DOB are all editable (the registry validates all three).
    if (!this.cpfCorrectionForm || !this.canSubmitCpfCorrection) return;

    this.isCpfCorrectionSubmitting = true;
    this.cpfCorrectionError = null;
    try {
      const cpfRaw = (this.correctedCpf || '').replace(/\D/g, '');
      const legalName = (this.correctedName || '').trim();
      const dobMoment = this.cpfCorrectionForm.get('dob')!.value;
      const dobIso = moment(dobMoment).format('YYYY-MM-DD');

      // Persist only what actually changed. Sending an unchanged legal_name would needlessly
      // trip the /profile identity-fraud guard (409 IDENTITY_FRAUD_DETECTED) when a previous
      // check was cancelled by cross-validation — the exact loop that stranded CPK-4310.
      const pii: any = {};
      if (cpfRaw !== (this.originalCpf || '').replace(/\D/g, '')) pii.id_document_value = cpfRaw;
      if (dobIso !== this.originalDob) pii.date_of_birth = dobIso;
      if (legalName && legalName !== (this.originalName || '').trim()) pii.legal_name = legalName;

      if (Object.keys(pii).length > 0) {
        await this.us.updateProfile({ pii });
      }

      this.cpfCorrectionForm = null;
      this.originalCpf = null;
      this.originalDob = null;
      this.originalName = null;
      this.correctedCpf = '';
      this.correctedName = '';
      // processError() resets state, clears the captured files, and returns to the capture/upload
      // step — i.e. the user resubmits a fresh document with the corrected details.
      this.processError();
    } catch (error) {
      console.error('[KYC] Error updating corrected details:', error);
      this.cpfCorrectionError = this.buildCpfCorrectionErrorMessage(error);
    } finally {
      this.isCpfCorrectionSubmitting = false;
    }
  }

  /**
   * Maps a failed correction submit to an already-translated inline message. The /profile
   * identity-fraud guard (409 IDENTITY_FRAUD_DETECTED) gets a specific explanation; anything
   * else falls back to a generic retry message.
   */
  private buildCpfCorrectionErrorMessage(error: unknown): string {
    const errorCode = (error as { errorCode?: string } | null)?.errorCode;
    return this.translate.instant(
      errorCode === 'IDENTITY_FRAUD_DETECTED'
        ? 'KycProfileUpdateBlockedSecurity'
        : 'KycProfileUpdateFailed'
    );
  }

  // ==================== NAVIGATION ====================

  nextStep() {
    switch (this.steps) {
      case 'TYPE-DOC':
        this.processDocumentType();
        break;
      case 'UPLOAD':
        this.processUpload();
        break;
      case 'ERROR':
        this.processError();
        break;
      default:
        break;
    }
  }

  backStep() {
    switch (this.steps) {
      case 'UPLOAD':
        this.steps = 'TYPE-DOC';
        break;
      case 'PROCESSING':
        // Can't go back while processing
        break;
      default:
        break;
    }
  }

  // ==================== VALIDATION ====================

  private validateFilesForUpload(): boolean {
    const uploads: File[] = [];

    if (this.frontImageFile) uploads.push(this.frontImageFile);
    if (this.backImageFile) uploads.push(this.backImageFile);

    if (uploads.length === 0) {
      this.currentError = this.messageService.getErrorDetails(
        'VALIDATION_FAILED',
        this.translate.instant('KYC.ERROR.NO_FILES_SELECTED')
      );
      return false;
    }

    // Front image is always required
    if (!this.frontImageFile) {
      this.currentError = this.messageService.getErrorDetails(
        'VALIDATION_FAILED',
        this.translate.instant('KYC.ERROR.FRONT_IMAGE_REQUIRED')
      );
      return false;
    }

    // Back image is required for two-sided documents (RG / ID card / etc.)
    if (this.requiresBackImage && !this.backImageFile) {
      this.currentError = this.messageService.getErrorDetails(
        'VALIDATION_FAILED',
        this.translate.instant('KYC.ERROR.BACK_IMAGE_REQUIRED')
      );
      return false;
    }

    return true;
  }

  // ==================== UPLOAD AND PROCESSING ====================

  protected async uploadAndProcess(preUploadedDocIds?: {
    frontId: string;
    backId?: string;
  }) {
    // Reset all processing state
    this.uploadAttempts = 0;
    this.hasProcessingError = false;
    this.errorInStep = null;
    this.currentError = null;
    this.isProcessingIndeterminate = true;
    this.initializeProcessingSteps();

    this.steps = 'PROCESSING';
    this.processing = ProcessingStatus.PROCESSING;
    if (this.isSomosV1) {
      // v2 stepper: the pre-submit phases (read + confirm) happened on the capture path — mark
      // them done so this (re)initialised list stays truthful, and fold the storage upload into
      // "Sending your verification" alongside the /kyc_check submit (there is no UPLOADING row).
      this.updateProcessingStep('READING', true);
      this.updateProcessingStep('CONFIRMING', true);
      this.currentProcessingStep = 'SUBMITTING';
      this.updateProcessingStep('SUBMITTING', false);
    } else {
      this.currentProcessingStep = 'UPLOADING';
      this.updateProcessingStep('UPLOADING', false);
    }

    try {
      // Step 1: Upload files with retry. The SomosKycV1 no-QR pre-check uploads the document
      // before this (so it can OCR the stored doc), then passes the ids back here to avoid a
      // second upload — reuse them when provided.
      const uploadedDocIds =
        preUploadedDocIds ?? (await this.uploadFilesWithRetry());
      this.updateProcessingStep('UPLOADING', true);

      // Step 2: Submit KYC check to lambda
      this.currentProcessingStep = 'SUBMITTING';
      this.updateProcessingStep('SUBMITTING', false);

      const kycCheckBody: KycCheckBody = {
        provider: (this.data.provider ?? 'verifflite') as any,
        documentType: this.getDocumentTypeForAPI(this.documentType!) as any,
        user_id: this.data.ref || (await this.us.getCurrentUser())!.id!,
        action_id: this.data.action_id,
        // CPK-4012: send the claim so the check is bound to it and the gate is per-claim.
        ...(this.data.claim_id ? { claim_id: this.data.claim_id } : {}),
        front_id: uploadedDocIds.frontId,
        ...(uploadedDocIds.backId && { back_id: uploadedDocIds.backId }),
        ...(this.cnhType || this.digitalPictureCapture || this.documentSignatureStatus
          ? {
              providerAdditionalInfo: {
                ...(this.cnhType ? { documentFormat: this.cnhType } : {}),
                ...(this.digitalPictureCapture ? { digitalPicture: true } : {}),
                ...(this.documentSignatureStatus
                  ? { documentSignature: this.documentSignatureStatus }
                  : {}),
              },
            }
          : {}),
      };

      console.log('[KYC] Submit KYC Check Body:', {
        provider: this.data.provider,
        documentTypeEnum: this.documentType,
        documentTypeForAPI: this.getDocumentTypeForAPI(this.documentType!),
        body: kycCheckBody,
      });

      this.trace.log('submit', 'info', 'Submitting KYC check', {
        endpoint: '/kyc_check',
        provider: kycCheckBody.provider,
        documentType: kycCheckBody.documentType,
        documentFormat: this.cnhType ?? 'n/a',
        digitalPicture: this.digitalPictureCapture,
        user_id: kycCheckBody.user_id,
        action_id: kycCheckBody.action_id ?? null,
        claim_id: this.data.claim_id ?? null,
        case_id: (this.data as any).case_id ?? null,
        front_id: kycCheckBody.front_id ?? null,
        back_id: (kycCheckBody as any).back_id ?? null,
        has_back: !!(kycCheckBody as any).back_id,
        agent_session: this.isAgentSession,
      });

      const submitStartedAt = Date.now();
      let kycCheckResponse = await this.ks.submitKycCheck(kycCheckBody);
      const submitLatencyMs = Date.now() - submitStartedAt;
      console.log('[KYC] Submit response:', kycCheckResponse);
      kycCheckResponse = kycCheckResponse.data;

      this.trace.log('submit', 'success', 'KYC check created', {
        checkId: kycCheckResponse?.id,
        stage: kycCheckResponse?.stage,
        provider: kycCheckResponse?.provider,
        provider_ref: kycCheckResponse?.provider_ref ?? null,
        passed: kycCheckResponse?.passed ?? null,
        latencyMs: submitLatencyMs,
      });

      this.updateProcessingStep('SUBMITTING', true);

      // Step 3: Start pre-processing
      this.currentProcessingStep = 'PRE_PROCESSING';
      this.updateProcessingStep('PRE_PROCESSING', false);
      this.updateProcessingStep('PROCESSING', false);

      // Store done_action_id for polling
      this.actionDoneActionId = kycCheckResponse.id;
      console.log('[KYC] Set actionDoneActionId:', {
        actionDoneActionId: this.actionDoneActionId,
        kycCheckResponseId: kycCheckResponse.id
      });

      if (this.isSomosV1) {
        // Stage-driven: reflect the check's real stage immediately, then keep it in sync on every
        // poll tick (see startActionPolling). No fake PRE_PROCESSING→PROCESSING timer.
        this.syncStepsFromStage(kycCheckResponse.stage);
      } else {
        // Simulate progression through PRE_PROCESSING to PROCESSING
        setTimeout(() => {
          this.updateProcessingStep('PRE_PROCESSING', true);
          this.currentProcessingStep = 'PROCESSING';
          this.startProcessingElapsedTimer();
        }, 2000); // Wait 2 seconds before moving to PROCESSING step
      }

      // Start polling if we have action_id
      if (this.data.action_id) {
        console.log('[KYC] Starting action polling', {
          actionId: this.data.action_id,
          checkId: kycCheckResponse.id,
        });
        this.startActionPolling();
        return; // Exit here - polling will handle completion
      }

      // Legacy flow without polling
      this.completeAllProcessingSteps();

      // Step 3: Analyze response
      if (!kycCheckResponse || !kycCheckResponse.stage) {
        // No stage info - treat as error
        throw new Error(
          'Invalid KYC check response - missing stage information'
        );
      }

      this.checkId = kycCheckResponse.id || '';

      if (
        kycCheckResponse.stage === 'PROCESSING' ||
        kycCheckResponse.stage === 'PRE_PROCESSING'
      ) {
        // Successfully submitted, will be processed asynchronously
        console.log('[KYC] Check submitted successfully - stage PROCESSING.');
        this.processing = ProcessingStatus.APPROVED;
        this.steps = 'SUCCESS';
        this.result = {
          status: 'PROCESSING',
          checkId: this.checkId,
          data: kycCheckResponse,
          message: this.translate.instant(
            'Your documents have been submitted successfully and are being processed.'
          ),
        };


      } else if (kycCheckResponse.stage === 'DONE') {
        // Already processed
        if (kycCheckResponse.passed === true) {
          console.log(
            '[KYC] Check completed successfully - stage DONE, passed true'
          );
          this.processing = ProcessingStatus.APPROVED;
          this.steps = 'SUCCESS';
          this.result = {
            status: 'VERIFIED',
            checkId: this.checkId,
            data: kycCheckResponse,
            message: this.translate.instant('identityVerified'),
          };


        } else {
          // Rejected
          console.log('[KYC] Check rejected - stage DONE, passed false');
          this.processing = ProcessingStatus.REJECTED;
          this.steps = 'ERROR';
          this.currentError =
            this.messageService.getErrorDetails('DOCUMENT_REJECTED');
          this.parseAgentDiagnostics(kycCheckResponse.notes);
          this.result = {
            status: 'REJECTED',
            checkId: this.checkId,
            data: kycCheckResponse,
            error: this.currentError,
          };
        }
      } else if (kycCheckResponse.stage === 'CANCELLED') {
        // [SECURITY] Check if CANCELLED due to SERPRO cross-validation failure
        const serproFailed =
          (kycCheckResponse as any)?.provider_return?.serpro_cross_validation_failed === true;

        this.processing = ProcessingStatus.REJECTED;
        this.steps = 'ERROR';

        if (serproFailed) {
          console.warn('[KYC] Check cancelled by SERPRO cross-validation — user must restart full KYC');
          this.currentError = {
            code: 'DOCUMENT_REJECTED',
            message: this.translate.instant('IdentityVerificationFailedMessage'),
            suggestedAction: this.translate.instant('IdentityVerificationFailedTitle'),
            canRetry: true,
          };
          this.result = {
            status: 'CANCELLED',
            checkId: this.checkId,
            data: kycCheckResponse,
            error: this.currentError,
            requiresFullRestart: true,
          };
        } else {
          console.log('[KYC] Check cancelled (generic)');
          this.currentError =
            this.messageService.getErrorDetails('DOCUMENT_REJECTED');
          this.result = {
            status: 'CANCELLED',
            checkId: this.checkId,
            data: kycCheckResponse,
            error: this.currentError,
          };
        }
      } else {
        // Unknown stage
        console.warn('[KYC] Unknown stage:', kycCheckResponse.stage);
        throw new Error(`Unknown KYC check stage: ${kycCheckResponse.stage}`);
      }


    } catch (error: any) {
      console.error('[KYC] Upload and process error:', error);

      // Handle API error response format
      // Check both error.error (HttpErrorResponse) and error.data formats
      const errorData = error?.error || error?.data;

      console.log('[KYC] Error data analysis:', {
        hasErrorData: !!errorData,
        errorDataError: errorData?.error,
        errorDataCode: errorData?.code,
        errorDataMessage: errorData?.message,
        fullErrorData: errorData ? JSON.stringify(errorData, null, 2) : 'N/A'
      });

      if (errorData && errorData.error === true) {
        // Check for specific error codes that need special handling
        if (errorData.code === 'KYC_RETRY_LIMIT_EXCEEDED') {
          this.currentError = this.processRetryLimitError(errorData.data);
          // Navigate to RETRY_LIMIT_EXCEEDED step
          this.steps = 'RETRY_LIMIT_EXCEEDED';
          this.loadClientDob();
          this.result = {
            status: 'ERROR',
            error: this.currentError,
          };
          return;
        } else if (errorData.code === 'KYC_USER_BLOCKED') {
          this.currentError = this.processUserBlockedError(errorData.data);
          // Navigate to RETRY_LIMIT_EXCEEDED step (same UI for blocked users)
          this.steps = 'RETRY_LIMIT_EXCEEDED';
          this.loadClientDob();
          this.result = {
            status: 'ERROR',
            error: this.currentError,
          };
          return;
        } else if (errorData.code === 'KYC_CHECK_CREATION_FAILED' && errorData.message?.includes('full name')) {
          // Erro de nome incompleto - abrir modal de correção
          console.log('[KYC] KYC_CHECK_CREATION_FAILED - User did not provide full name', errorData);

          this.currentError = {
            code: 'LEGAL_NAME_MISMATCH',
            message: this.translate.instant('KYC_NAME_VALIDATION_FAILED'),
            suggestedAction: this.translate.instant('KYC_NAME_VALIDATION_FAILED_ACTION'),
            canRetry: true,
            requiresModal: true,
            validationType: 'legal_name_match',
            technicalDetails: errorData.message,
            data: {
              source: 'check_creation',
              userData: errorData.data,
              errorCode: errorData.code,
              validationFailed: true
            },
          };

          // Parar spinner e marcar step como erro
          this.hasProcessingError = true;
          this.errorInStep = this.currentProcessingStep;
          this.processing = ProcessingStatus.REJECTED;
          this.isProcessingIndeterminate = false;
          this.updateProcessingStep(this.currentProcessingStep, false, true);

          // Abrir modal de correção de nome legal (this.currentError já foi setado acima)
          if (this.currentError) {
            this.openLegalNameFixModal(this.currentError);
          }
          return;
        } else {
          this.currentError = this.buildBackendError(errorData);

          // Start wait timer if shouldWait is true
          if (this.currentError.shouldWait) {
            this.startWaitTimer();
          }
        }
      } else {
        // Fallback for other error formats
        this.currentError = this.messageService.parseBackendError(error);
      }

      // Set processing error state instead of changing to ERROR step
      this.hasProcessingError = true;
      this.errorInStep = this.currentProcessingStep;
      this.processing = ProcessingStatus.REJECTED;

      // Mark current step as failed
      this.updateProcessingStep(this.currentProcessingStep, false, true);

      // 🔄 Verificar se atingiu limite de retries IMEDIATAMENTE após falha
      this.checkRetryLimitAfterFailure();

      // Keep in PROCESSING step to show error inline
      // this.steps = 'ERROR'; // Removed: Don't change step
      this.result = {
        status: 'ERROR',
        error: this.currentError,
      };
    }finally{
      await this.sendKycEvent();

    }
  }

  private async sendKycEvent(): Promise<any>{

    const claimId = this.data.claim_id;
    const eventTypeValue: EventTypes = EventTypes.KYC_PROCESS_LOAD_PAGE;

    try{
      await this.eventService.sendEventWithCaseId(eventTypeValue, claimId);
    }catch(error) {
      console.error('Event not sended, ', error);
    }
  }

  protected async uploadFilesWithRetry(): Promise<{
    frontId: string;
    backId?: string;
  }> {
    let lastError: any;

    for (let attempt = 0; attempt < this.retryConfig.maxAttempts; attempt++) {
      try {
        this.uploadAttempts = attempt + 1;

        const uploads: File[] = [];
        const metas: any[] = [];
        if (this.frontImageFile) { uploads.push(this.frontImageFile); metas.push(this.frontFileMetadata); }
        if (this.backImageFile) { uploads.push(this.backImageFile); metas.push(this.backFileMetadata); }

        console.log('[KYC] Uploading files:', {
          provider: this.data.provider,
          documentTypeEnum: this.documentType,
          documentTypeValue: this.documentType,
          documentTypeForAPI: this.getDocumentTypeForAPI(this.documentType!),
          filesCount: uploads.length,
        });

        const uploadPromises = uploads.map((f: File, i: number) => {
          return this.pds.upload(
            f,
            this.data.ref,
            this.getDocumentTypeForAPI(this.documentType!),
            this.data.bucket,
            i,
            metas[i] ?? null,
          ) as Promise<UploadReturn>;
        });

        const results = await Promise.all(uploadPromises);

        // Validate upload results
        if (!results[0] || !results[0].data || !results[0].data.doc?.id) {
          throw new Error('Front document upload failed');
        }

        const frontId = results[0].data.doc.id;
        let backId: string | undefined;

        if (results.length > 1) {
          if (!results[1] || !results[1].data || !results[1].data.doc?.id) {
            throw new Error('Back document upload failed');
          }
          backId = results[1].data.doc.id;
        }

        return { frontId, backId };
      } catch (error) {
        lastError = error;
        console.warn(`[KYC] Upload attempt ${attempt + 1} failed:`, error);

        // Wait before retry (exponential backoff)
        if (attempt < this.retryConfig.maxAttempts - 1) {
          const delay = Math.min(
            this.retryConfig.initialDelayMs *
            Math.pow(this.retryConfig.backoffMultiplier, attempt),
            this.retryConfig.maxDelayMs
          );
          await this.sleep(delay);
        }
      }
    }

    // All attempts failed
    throw lastError || new Error('Upload failed after all retry attempts');
  }

  /**
   * Optional: Poll KYC check status
   * This method can be called externally if needed to check status of PROCESSING checks
   * It's not part of the main upload flow anymore
   */
  protected async pollKycCheckStatus(checkId: string): Promise<void> {
    const startTime = Date.now();
    let attempt = 0;

    while (attempt < this.pollingConfig.maxAttempts) {
      // Check timeout
      if (Date.now() - startTime > this.pollingConfig.timeoutMs) {
        console.warn('[KYC] Polling timeout reached');
        this.processing = ProcessingStatus.END_PROCESSING;
        this.steps = 'END_PROCESSING';
        this.result = {
          status: 'PENDING',
          checkId,
        };
        return;
      }

      try {
        const kycCheck = await this.ks.getById(checkId);
        console.log(`[KYC] Poll attempt ${attempt + 1}:`, kycCheck.stage);

        if (kycCheck.stage === 'DONE') {
          // Process complete
          if (kycCheck.passed) {
            this.processing = ProcessingStatus.APPROVED;
            this.steps = 'SUCCESS';
            this.result = {
              status: 'VERIFIED',
              checkId,
              data: kycCheck,
              message: this.translate.instant('identityVerified'),
            };
          } else {
            // Tenta buscar erro detalhado em notes
            // 1º: formato novo (Veriff Lite) — n.data.status + n.data.declinedReason
            // 2º: formato antigo (Veriff standard) — n.action + n.nextAction (fallback)
            const detailedError = this.processVeriffLiteError(kycCheck.notes, kycCheck.provider_return)
              || this.processLegacyNoteError(kycCheck.notes)
              || null;
            this.processing = ProcessingStatus.REJECTED;
            this.steps = 'ERROR';
            this.currentError = detailedError || this.messageService.getErrorDetails('DOCUMENT_REJECTED');
            this.parseAgentDiagnostics(kycCheck.notes);
            this.result = {
              status: 'REJECTED',
              checkId,
              error: this.currentError,
            };
          }
          return;
        }
      } catch (error) {
        console.error('[KYC] Poll error:', error);
        // Continue polling even if one request fails
      }

      // Wait before next poll (exponential backoff)
      const delay = Math.min(
        this.pollingConfig.initialIntervalMs *
        Math.pow(this.pollingConfig.backoffMultiplier, attempt),
        this.pollingConfig.maxIntervalMs
      );

      await this.sleep(delay);
      attempt++;
    }

    // Max attempts reached
    console.warn('[KYC] Max polling attempts reached');
    this.processing = ProcessingStatus.END_PROCESSING;
    this.steps = 'END_PROCESSING';
    this.result = {
      status: 'PENDING',
      checkId,
    };
  }

  // ==================== PROCESSING STEPS MANAGEMENT ====================

  protected initializeProcessingSteps() {
    // SomosKycV1 (v2): the unified five-step stepper (read → confirm → send → validate → analyze).
    // Steps 1-3 are frontend-driven from the capture/submit path (see somosStepperStart /
    // somosStepAdvance and uploadAndProcess); steps 4-5 are stage-driven (syncStepsFromStage).
    // All other providers keep the messageService 4-step list verbatim.
    if (this.isSomosV1) {
      // Each step carries its own hint; the template renders it only while the step is active, and
      // syncStepsFromStage re-resolves the keys once the stage-driven phase (post-submit) begins.
      this.processingSteps = SOMOS_V1_STEP_ORDER.map((step) => ({
        step,
        message: SOMOS_V1_STEP_LABEL_KEYS[step],
        done: false,
        hasError: false,
        actionNeeded: false,
        descriptionKey: SOMOS_V1_STEP_HINT_KEYS[step],
      }));
      return;
    }
    this.processingSteps = this.messageService
      .getProcessingSteps()
      .map((item) => ({
        ...item,
        done: false,
        hasError: false,
      }));
  }

  /**
   * SomosKycV1 only: show the stepper the moment the guided capture completes, so the pre-submit
   * phases (pre-check upload, OCR read, confirmation modals) run over a visible step list instead
   * of behind the capture screen. Starts on READING; the capture path advances from there.
   */
  protected somosStepperStart(): void {
    if (!this.isSomosV1) return;
    this.hasProcessingError = false;
    this.errorInStep = null;
    this.currentError = null;
    this.isProcessingIndeterminate = true;
    this.processing = ProcessingStatus.PROCESSING;
    this.initializeProcessingSteps();
    this.currentProcessingStep = 'READING';
    this.steps = 'PROCESSING';
  }

  /** SomosKycV1 only: complete `done` and move the spinner to `next`. */
  protected somosStepAdvance(done: SomosV1ProcessingStep, next: SomosV1ProcessingStep): void {
    if (!this.isSomosV1) return;
    this.updateProcessingStep(done, true);
    this.currentProcessingStep = next;
  }

  /**
   * SomosKycV1 only: flag/unflag a step as paused waiting on the user (amber row + "we need your
   * confirmation" sub-line) — used while a pre-submit decision modal is open over the stepper.
   */
  protected somosStepActionNeeded(step: SomosV1ProcessingStep, needed: boolean): void {
    if (!this.isSomosV1) return;
    const s = this.processingSteps.find((p) => p.step === step);
    if (!s) return;
    s.actionNeeded = needed;
    s.descriptionKey = needed ? SOMOS_V1_ACTION_NEEDED_HINT_KEY : SOMOS_V1_STEP_HINT_KEYS[step];
  }

  /**
   * SomosKycV1 only: map the observed check stage to the five completion-step states and apply it.
   * Called on every poll tick (and once right after submit) so the list mirrors the real pipeline
   * instead of the fake 2s timer used by the other providers. Never disturbs a step already flagged
   * as errored — the failure handlers own that state.
   */
  private syncStepsFromStage(stage?: string): void {
    if (!this.isSomosV1 || this.hasProcessingError) return;
    const sync = mapSomosV1StageToSteps(stage);
    if (!sync) return; // Unknown/unhandled stage → leave the current states untouched.

    this.currentProcessingStep = sync.currentStep;
    for (const target of sync.steps) {
      const s = this.processingSteps.find((p) => p.step === target.step);
      if (!s) continue;
      s.done = target.done;
      s.actionNeeded = target.actionNeeded;
      s.descriptionKey = target.descriptionKey;
    }
  }

  /**
   * SomosKycV1 only: flag the "Validating your personal data" step as action-needed (amber) while
   * the inline CPF-correction form is open (registry declined), so the step list explains why
   * nothing is spinning. Cleared on resubmit when initializeProcessingSteps() rebuilds the list.
   */
  protected markSomosPersonalDataActionNeeded(): void {
    if (!this.isSomosV1) return;
    // Reuse the name-gate layout: every step before personal-data done, personal-data action-needed,
    // analysis pending — clearing any error flag so the row reads amber (correctable) rather than
    // red (hard failure).
    const sync = mapSomosV1StageToSteps('AWAITING_NAME_CONFIRMATION');
    if (!sync) return;
    this.currentProcessingStep = sync.currentStep;
    for (const target of sync.steps) {
      const s = this.processingSteps.find((p) => p.step === target.step);
      if (!s) continue;
      s.done = target.done;
      s.hasError = false;
      s.actionNeeded = target.actionNeeded;
      s.descriptionKey = target.descriptionKey;
    }
  }

  protected updateProcessingStep(step: ProcessingStep, done: boolean, hasError?: boolean) {
    const stepIndex = this.processingSteps.findIndex((s) => s.step === step);
    if (stepIndex !== -1) {
      this.processingSteps[stepIndex].done = done;
      if (hasError !== undefined) {
        this.processingSteps[stepIndex].hasError = hasError;
      }
    }
  }

  private completeAllProcessingSteps() {
    this.processingSteps.forEach((step) => (step.done = true));
  }

  // ==================== HELPER METHODS ====================

  protected sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  protected getDocumentTypeForAPI(docType: DocumentTypeEnum): string {
    // Special handling for Veriff/Veriff Lite (+ Somos KYC clone): convert driving_licence to drivers_license
    if (this.data.provider === 'verifflite' || this.data.provider === 'veriff' || this.data.provider === 'somoskyc_v1') {
      if (docType === DocumentTypeEnum.DRIVER_LICENSE) {
        return 'drivers_license';
      }
    }

    switch (docType) {
      case DocumentTypeEnum.PASSPORT:
        return 'passport';
      case DocumentTypeEnum.DRIVERS_LICENSE:
        return 'drivers_license'; // Veriff/Veriff Lite format
      case DocumentTypeEnum.ID_CARD:
        return 'id_card';
      case DocumentTypeEnum.UNKNOWN:
        return 'unknown';
      case DocumentTypeEnum.DRIVER_LICENSE:
        return 'driving_licence'; // Legacy/manual providers format
      case DocumentTypeEnum.NATIONAL_IDENTITY_CARD:
        return 'national_identity_card';
      case DocumentTypeEnum.RESIDENCE_PERMIT:
        return 'residence_permit';
      default:
        return 'passport';
    }
  }

  getDocumentTypeLabel(type: DocumentTypeEnum): string {
    return this.options.find((opt) => opt.value === type)?.label || '';
  }

  getProviderPrivacyNotice(): string {
    return this.messageService.getProviderPrivacyNotice(this.data.provider);
  }

  /**
   * Verifica se o erro é B2b ou B3b (Veriff reading error)
   * B2b/B3b = Veriff está errada, nome legal está correto
   */
  get isVeriffReadingError(): boolean {
    const category = this.storedMismatchCategory || this.data.mismatchCategory;
    const subCategory = this.storedMismatchSubCategory || this.data.mismatchSubCategory;
    return (category === 'B2' || category === 'B3') && subCategory === 'b';
  }

  // ==================== ACTION POLLING ====================

  /**
   * Inicia polling para verificar o status da action
   */
  protected startActionPolling(): void {
    if (!this.data.action_id) {
      console.error('[KYC] Cannot start polling - no action_id');
      return;
    }

    console.log('[KYC] Starting action polling', {
      actionId: this.data.action_id,
      doneActionId: this.actionDoneActionId,
    });

    const pollConfig = {
      actionId: this.data.action_id,
      doneActionId: this.actionDoneActionId,
      claimId: this.data.claim_id,
      actionType: 'KYC' as const,
      initialDelay: 2000,      // 2s
      maxDelay: 30000,         // 30s max
      backoffMultiplier: 1.5,  // Exponential backoff
      maxAttempts: 20          // ~10 minutes total
    };

    // Fresh poll loop (also entered when resuming after the SERPRO name gate) — reset the
    // transition tracker so the first observed status registers as a stage change.
    this.lastPollStatus = null;
    this.trace.log('poll', 'info', 'Polling started', {
      actionId: pollConfig.actionId,
      checkId: this.actionDoneActionId ?? null,
      initialDelayMs: pollConfig.initialDelay,
      maxDelayMs: pollConfig.maxDelay,
      backoffMultiplier: pollConfig.backoffMultiplier,
      maxAttempts: pollConfig.maxAttempts,
    });

    this.pollingSubscription = this.actionPollingService.poll(pollConfig).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (result: PollingResult) => {
        console.log('[KYC] Polling result received', {
          actionId: this.data.action_id,
          status: result.status,
          completed: result.completed,
          attempt: result.attempt,
        });

        // Trace every tick, flagging stage transitions (PROCESSING → AWAITING_NAME_CONFIRMATION →
        // DONE, …). A terminal/awaiting status raises the level so it stands out on the timeline.
        const transition = result.status !== this.lastPollStatus;
        const level: KycTraceLevel = result.completed
          ? 'success'
          : ['AWAITING_NAME_CONFIRMATION', 'RESUBMISSION', 'CANCELED', 'ERROR'].includes(result.status)
            ? 'warn'
            : 'info';
        this.trace.log(
          'poll',
          level,
          transition ? `Stage → ${result.status}` : `Poll #${result.attempt ?? '?'}: ${result.status}`,
          {
            status: result.status,
            previousStatus: transition ? this.lastPollStatus : undefined,
            attempt: result.attempt ?? null,
            completed: result.completed,
          },
        );
        this.lastPollStatus = result.status;

        // SomosKycV1 only: keep the completion-step list in sync with the real check stage on every
        // tick (fall back to the derived status when the stage couldn't be read). The success/failure
        // branches below then take over for terminal stages.
        if (this.isSomosV1) {
          this.syncStepsFromStage(result.checkStage ?? result.status);
        }

        if (result.completed) {
          // ✅ KYC passou (ou FAIL adiável via postpone) - sucesso!
          console.log('[KYC] Polling completed successfully - KYC passed/postponed');
          const kycData = await this.fetchKycCheckData(this.actionDoneActionId);
          if (await this.maybePromptNameFixBeforeTerminal(kycData)) return;
          this.handlePollingSuccess(kycData?.notes, kycData?.provider_return, kycData?.passed);
        } else if (this.isSomosV1 && result.status === 'AWAITING_NAME_CONFIRMATION') {
          // 🇧🇷 SERPRO name gate (SomosKycV1 only): paused before the document step — the official
          // SERPRO name didn't match the name the user provided. Ask them to accept it.
          console.log('[KYC] Polling detected AWAITING_NAME_CONFIRMATION - opening SERPRO name modal');
          await this.handleSerproNameConfirmation();
        } else if (result.status === 'RESUBMISSION') {
          // ❌ KYC solicita reenvio de documentos - busca notes/provider_return e trata
          console.log('[KYC] Polling detected RESUBMISSION - treating as failed validation');
          const kycData = await this.fetchKycCheckData(this.actionDoneActionId);
          this.handlePollingFailure(this.translate.instant('KYC.ERROR.RESUBMISSION_REQUIRED'), kycData?.notes, kycData?.provider_return);
        } else if (result.status === 'DONE' && !result.completed) {
          // ❌ KYC falhou (status DONE mas passed=false) - busca notes e provider_return
          console.log('[KYC] Polling completed - KYC failed validation');
          const kycData = await this.fetchKycCheckData(this.actionDoneActionId);
          if (await this.maybePromptNameFixBeforeTerminal(kycData)) return;
          this.handlePollingFailure(this.translate.instant('KYC.ERROR.VERIFICATION_FAILED'), kycData?.notes, kycData?.provider_return);
        } else if (this.isSomosV1 && (result.status === 'CANCELED' || result.status === 'ERROR')) {
          // ❌ Check cancelado/erro (stage CANCELLED/ERROR) - mostra falha com as notas.
          // SomosKycV1 only — other providers keep their prior behaviour (poll on until timeout).
          console.log('[KYC] Polling stopped - check', result.status);
          const kycData = await this.fetchKycCheckData(this.actionDoneActionId);
          this.handlePollingFailure(this.translate.instant('KYC.ERROR.VERIFICATION_FAILED'), kycData?.notes, kycData?.provider_return);
        }
      },
      error: (err: unknown) => {
        const errMessage = err instanceof Error ? err.message : String(err);
        // Distinguish "we ran out of polling attempts" (provider simply hasn't resolved yet)
        // from a genuine polling error (network/lookup failure). The polling service throws a
        // typed PollingExhaustedError when it exhausts its window (~10 min); branch on the type
        // (with a code fallback for cross-bundle `instanceof` gaps) rather than the message text.
        const isExhaustion =
          err instanceof PollingExhaustedError ||
          (err as { code?: string } | null)?.code === 'POLLING_EXHAUSTED';
        console.error('[KYC] Polling error', {
          actionId: this.data.action_id,
          error: errMessage,
          exhaustion: isExhaustion,
        });
        this.trace.log(
          'poll',
          isExhaustion ? 'warn' : 'error',
          isExhaustion ? 'Polling window exhausted — still reviewing' : 'Polling error / timeout',
          {
            actionId: this.data.action_id,
            lastStatus: this.lastPollStatus,
            error: errMessage,
          },
        );

        if (isExhaustion) {
          // ⏳ The provider never resolved within the polling window. This is NOT a rejection —
          // the review is still in progress and will notify the user when it finishes. Land on the
          // calm END_PROCESSING terminal (mirrors the legacy pollKycCheckStatus timeout branch)
          // so the user can close the modal without it looking like their document was declined.
          this.processing = ProcessingStatus.END_PROCESSING;
          this.steps = 'END_PROCESSING';
          this.result = {
            status: 'PENDING',
            checkId: this.actionDoneActionId,
          };
          return;
        }

        // Genuine polling error (network/lookup) → keep the hard failure path.
        this.handlePollingFailure(this.translate.instant('KYC.ERROR.STATUS_VERIFICATION_FAILED'));
      }
    });
  }

  /**
   * 🇧🇷 SERPRO name gate: the flow paused before the document step because the
   * authoritative SERPRO (CPF) name didn't match the name the user provided. Show
   * the confirmation modal; on accept, adopt the SERPRO name and resume polling
   * (the backend continues into the Veriff document step); on reject, the KYC
   * fails and the user must restart with correct details.
   */
  protected async handleSerproNameConfirmation(): Promise<void> {
    const checkId = this.actionDoneActionId;
    if (!checkId) {
      console.error('[KYC] SERPRO name confirmation: missing kyc_check id');
      this.handlePollingFailure(this.translate.instant('KYC.ERROR.STATUS_VERIFICATION_FAILED'));
      return;
    }

    const kycData = await this.fetchKycCheckData(checkId);
    const awaiting = (kycData?.provider_return as any)?.awaiting_name_confirmation ?? {};

    // System proposal: the authoritative SERPRO name differs from the name on file.
    this.trace.log('precheck', 'warn', 'SERPRO name mismatch — proposing the registry name', {
      onFileName: awaiting.currentName ?? null,
      proposedName: awaiting.suggestedName ?? null,
      reason: awaiting.reason ?? null,
      classification: awaiting.classification ?? null,
    });

    const ref = this.dialog.open(SerproNameConfirmModalComponent, {
      data: {
        currentName: awaiting.currentName ?? null,
        serproName: awaiting.suggestedName ?? '',
        documentTitle: this.documentType,
      } as SerproNameConfirmData,
      width: '90vw',
      maxWidth: '480px',
      maxHeight: '90vh',
      disableClose: true,
    });

    const decision = (await firstValueFrom(ref.afterClosed())) as SerproNameConfirmResult | undefined;
    if (!decision) {
      // Dismissed (disableClose makes this unlikely) — re-poll so we don't get stuck.
      this.trace.log('precheck', 'warn', 'SERPRO name modal dismissed', null);
      this.startActionPolling();
      return;
    }

    this.trace.log(
      'precheck',
      decision.accept ? 'success' : 'warn',
      decision.accept ? 'User ACCEPTED the SERPRO name (adopting it)' : 'User REJECTED the SERPRO name',
      { accept: decision.accept, proposedName: awaiting.suggestedName ?? null },
    );

    try {
      await this.ks.confirmSerproName(checkId, decision.accept);
    } catch (err) {
      console.error('[KYC] SERPRO name confirmation failed:', err);
      this.handlePollingFailure(this.translate.instant('KYC.ERROR.STATUS_VERIFICATION_FAILED'));
      return;
    }

    if (decision.accept) {
      // Backend resumed into the document step — poll again for the final result.
      this.startActionPolling();
      return;
    }

    // Rejected (TC3) — the backend decides the outcome from the action config:
    //  • postpone (DONE + canProceed) → the action tolerates the rejection → proceed under review.
    //  • CANCELLED + amendBeforeRestart → offer "amend your details", then restart the KYC.
    const after = await this.fetchKycCheckData(checkId);
    if (after?.stage === 'DONE' && notesAllowProceed(after?.notes)) {
      this.trace.log('precheck', 'warn', 'SERPRO name rejected but action allows proceed (postpone)', null);
      this.handlePollingSuccess(after?.notes, after?.provider_return, after?.passed);
      return;
    }
    if ((after?.provider_return as any)?.serpro_name_confirmation?.amendBeforeRestart) {
      this.trace.log('precheck', 'warn', 'SERPRO name rejected — offering amend-details before restart', null);
      this.openAmendDetailsAndRestart(awaiting.currentName ?? null);
      return;
    }
    // Fallback — terminal failure; instruct the user to restart with correct details.
    this.handlePollingFailure(this.translate.instant('KYC.ERROR.SERPRO_NAME_REJECTED'));
  }

  /**
   * TC3/TC4 — opens the standalone PersonalDetailsModal (plain editable legal
   * name / DOB / CPF form, no leak/SERPRO check → POST /profile) and, on save,
   * restarts the KYC from the capture step so a fresh check is created. The
   * prior check is already CANCELLED, so no revalidation is attempted — the
   * modal simply closes `{ updated: true }`.
   */
  protected openAmendDetailsAndRestart(currentName: string | null): void {
    const dialogRef = this.dialog.open(PersonalDetailsModalComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        userId: this.data.ref,
        config: {
          legalName: { show: true, required: true, initialValue: currentName || '' },
          dateOfBirth: { show: true, required: true },
          cpf: { show: true, required: true },
        },
      } as PersonalDetailsDialogData,
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.updated) {
          // Details amended → restart the KYC from the capture step (creates a new check).
          this.hasProcessingError = false;
          this.currentError = null;
          this.steps = 'TYPE-DOC';
          this.clearAllFiles();
        } else {
          // User closed the amend form without saving → show the terminal failure.
          this.handlePollingFailure(this.translate.instant('KYC.ERROR.SERPRO_NAME_REJECTED'));
        }
      });
  }

  /**
   * Busca os notes e provider_return do KYC check
   */
  protected async fetchKycCheckData(kycCheckId?: string): Promise<{ stage?: string, passed?: boolean, notes?: any[], provider_return?: any } | undefined> {
    if (!kycCheckId) {
      return undefined;
    }

    try {
      const kycCheck = await this.ks.getById(kycCheckId);
      console.log('[KYC] fetchKycCheckData - KYC Check data:', {
        id: kycCheck?.id,
        stage: kycCheck?.stage,
        hasNotes: !!kycCheck?.notes,
        notesCount: kycCheck?.notes?.length,
        hasProviderReturn: !!kycCheck?.provider_return,
        providerReturnKeys: kycCheck?.provider_return ? Object.keys(kycCheck.provider_return) : []
      });

      return {
        stage: kycCheck?.stage,
        passed: kycCheck?.passed,
        notes: kycCheck?.notes,
        provider_return: kycCheck?.provider_return
      };
    } catch (error) {
      console.error('[KYC] Error fetching kyc check data:', error);
      return undefined;
    }
  }

  /**
   * Manipula sucesso do polling
   *
   * 🎯 AGENT CLIENT MODE: Este método também é chamado quando um usuário está
   * flagado como agent_client e o KYC falha - nesse caso, tratamos a falha
   * como sucesso (non-blocking behavior).
   */
  protected handlePollingSuccess(notes?: any[], providerReturn?: any, passed?: boolean): void {
    this.pollingSubscription?.unsubscribe();
    this.stopProcessingElapsedTimer();
    this.completeAllProcessingSteps();

    this.processing = ProcessingStatus.APPROVED;
    this.steps = 'SUCCESS';

    // Screen precedence: a check that genuinely PASSED is VERIFIED ("Identity Verified"), even when a
    // corroboration-released possible_match still carries a canProceed note. Only a FAILED-but-
    // postponable check (passed !== true AND notes.postpone_reason.canProceed) shows the "under
    // review" message. `passed` is undefined for the agent-skip / legacy callers → treated as
    // not-passed so their existing postpone behaviour is unchanged.
    this.isPostpone = passed !== true && notesAllowProceed(notes);
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'en')
      .toUpperCase().startsWith('PT') ? 'PT' : 'EN';
    this.postponeMessage = this.isPostpone ? getPostponeMessage(notes, lang) : '';
    this.postponeBlocks = this.isPostpone ? getPostponeBlocks(notes) : null;
    // TC6: only document-readability postpones offer the resubmit-vs-manual-review choice.
    // CPK-4325: a registry(SERPRO)-forced postpone reuses the could_not_get_cpf token, but
    // resubmitting can never change its outcome (the check finalizes FAIL+canProceed no matter
    // the document) — render the plain postpone screen, exactly like any other postpone.
    this.postponeOffersRetry = postponeOffersRetryChoice(this.isPostpone, notes, providerReturn);

    this.trace.log(
      'result',
      this.isPostpone ? 'warn' : 'success',
      this.isPostpone ? 'KYC failed but allowed to proceed (postpone / under review)' : 'KYC verified ✓',
      {
        checkId: this.checkId,
        postpone: this.isPostpone,
        message: this.postponeMessage || null,
        ...decodeKycOutcome(notes, providerReturn),
      },
    );
    this.flushTraceDelta();

    this.result = {
      status: 'VERIFIED',
      checkId: this.checkId,
      message: this.isPostpone
        ? (this.postponeMessage || this.translate.instant('KycUnderReviewMessage'))
        : this.translate.instant('identityVerified'),
    };
  }

  /**
   * REVIEW terminal — a FAILED, non-postponable compliance/watchlist block. Renders the
   * manual-review card (same layout as the success/under-review terminal, NO action button):
   * "We'll review your case and get back to you". Copy comes from the note's `review_reason`
   * (data.review_reason.{messages,blocks}, injected by the backend from cp_actions.config.review),
   * falling back to the default KycReviewTitle / KycReviewMessage i18n when the action configured
   * none. Both the polling path (handlePollingFailure) and the reopen path
   * (checkAndSetVeriffLiteError) route here so the same verdict renders the same screen.
   */
  protected handleReviewTerminal(notes?: any[], providerReturn?: any): void {
    this.pollingSubscription?.unsubscribe();
    this.stopProcessingElapsedTimer();
    this.completeAllProcessingSteps();

    this.processing = ProcessingStatus.REJECTED;
    this.steps = 'REVIEW';

    const lang = (this.translate.currentLang || this.translate.defaultLang || 'en')
      .toUpperCase().startsWith('PT') ? 'PT' : 'EN';
    this.reviewMessage = getReviewMessage(notes, lang);
    this.reviewBlocks = getReviewBlocks(notes);

    // Agent impersonation still sees the diagnostics it saw on the old BLOCKED card.
    this.parseAgentDiagnostics(notes);

    this.trace.log('result', 'warn', 'KYC blocked — routed to manual-review terminal', {
      checkId: this.checkId,
      hasReviewReason: !!this.reviewMessage || !!this.reviewBlocks,
      ...decodeKycOutcome(notes, providerReturn),
    });
    this.flushTraceDelta();

    this.result = {
      status: 'REVIEW',
      checkId: this.checkId,
    };
  }

  /**
   * TC6 — the user chose to RESUBMIT a clearer document instead of accepting the
   * manual-review postpone. Restart the capture flow (a fresh submission on the
   * same check via the resubmission path).
   */
  protected onPostponeResubmit(): void {
    this.trace.log('result', 'warn', 'User chose to resubmit rather than accept manual review (TC6)', null);
    this.isPostpone = false;
    this.postponeOffersRetry = false;
    this.processError();
  }

  /**
   * TC6 — the user gave informed consent to refer the check to manual review.
   * Proceed (the postpone note already allows it).
   */
  protected onPostponeAcceptManualReview(): void {
    this.trace.log('result', 'warn', 'User consented to manual review (TC6)', null);
    this.postponeOffersRetry = false;
    this.close();
  }

  /**
   * SomosKycV1: when a terminal check still carries a CONFIRM_AUTO_ACTION name mismatch (adopt the
   * official SERPRO name), show the name-fix popup BEFORE the terminal screen — even when the
   * document was declined/postponed (which would otherwise skip it). Returns true if it took over
   * the terminal routing; false to let the caller show the normal terminal screen.
   */
  protected async maybePromptNameFixBeforeTerminal(
    kycData?: { notes?: any[]; provider_return?: any },
  ): Promise<boolean> {
    if (!this.isSomosV1 || !kycData) return false;
    const checkId = this.checkId || this.actionDoneActionId || '';
    if (!checkId || this.nameFixPrompted.has(checkId)) return false;
    if (this.ks.getNamesMismatchAction(kycData as any) !== 'CONFIRM_AUTO_ACTION') return false;

    this.nameFixPrompted.add(checkId); // once per check, regardless of the user's choice

    const suggested =
      this.ks.getNamesMismatchFromNotes(kycData as any)?.suggestedCorrection ||
      this.ks.getNamesMismatchFromProviderReturn(kycData as any)?.mismatchMetadata?.suggestedCorrection ||
      '';
    const confirmed = await confirmAdoptSerproName(
      this.dialog,
      suggested,
      this.translate,
    );
    if (!confirmed) return false; // reject → fall through to the original terminal screen

    // Confirm → adopt the SERPRO name, then re-route on fresh data (name now matches; the document
    // decision — pass or postpone "under review" — decides the terminal screen). If the revalidate/
    // refetch fails (network/HTTP), don't strand the user on the spinner: return false so the caller
    // shows its original terminal screen.
    try {
      await this.ks.revalidateKycCheck(checkId, true);
      const fresh = await this.ks.getById(checkId);
      if (decideTerminalAfterRevalidate(fresh) === 'success') {
        this.handlePollingSuccess(fresh?.notes, fresh?.provider_return, fresh?.passed);
      } else {
        this.handlePollingFailure(
          this.translate.instant('KYC.ERROR.VERIFICATION_FAILED'),
          fresh?.notes,
          fresh?.provider_return,
        );
      }
      return true;
    } catch (e) {
      console.error('[KYC] name-fix revalidate failed; falling back to terminal screen', e);
      return false;
    }
  }

  /**
   * Manipula falha do polling
   * Agora aceita notes e providerReturn opcionais para processar erros do Veriff Lite
   */
  protected async handlePollingFailure(message: string, notes?: any[], providerReturn?: any): Promise<void> {
    this.stopProcessingElapsedTimer();
    this.trace.log('result', 'error', 'KYC failed', {
      checkId: this.checkId,
      message,
      lastStatus: this.lastPollStatus,
      ...decodeKycOutcome(notes, providerReturn),
    });
    this.flushTraceDelta();
    console.log('[KYC] handlePollingFailure - Called with:', {
      message,
      notesCount: notes?.length,
      hasProviderReturn: !!providerReturn,
      actionDoneActionId: this.actionDoneActionId,
      checkId: this.checkId
    });
    this.pollingSubscription?.unsubscribe();

    // Parse agent diagnostics if this is an agent impersonation session
    this.parseAgentDiagnostics(notes);

    const agentReviewCheck = this.checkAgentReviewRequired(notes);

    if (agentReviewCheck.isOnlyError && agentReviewCheck.hasAgentReview) {
      console.log('[KYC] ✅ Only agent_review_required error found - showing confirmation modal');

      // Usa actionDoneActionId que é o ID do KYC check no banco
      const kycCheckId = this.actionDoneActionId || this.checkId;
      console.log('[KYC] Using kycCheckId for agent review:', kycCheckId);

      // Processa o agent review error (vai para modal de confirmação)
      this.processAgentReviewError(notes || [], kycCheckId);
      return; // NÃO continuar com fluxo de erro
    }

    // Se tem agent_review MAS também tem outros erros, ignora agent_review por enquanto
    // e processa os outros erros primeiro
    if (agentReviewCheck.hasAgentReview && agentReviewCheck.otherErrors.length > 0) {
      console.log('[KYC] Agent review found but has other errors - processing other errors first');
      // Continua processando os outros erros normalmente
    }

    // Genuine compliance/watchlist block (passed=false, not postponable): route to the REVIEW
    // terminal (manual-review card) — mirroring the reopen path — instead of the generic inline
    // error card. Placed before processVeriffLiteError so a watchlist/review note isn't reinterpreted
    // as a correctable document error. Correctable failures (resubmission / legal-name / CPF) don't
    // carry a review_reason or watchlist indicator, so they keep their existing flows.
    if (this.checkPepSanctionsError(notes) || notesRequireReview(notes)) {
      console.log('[KYC] handlePollingFailure - compliance/watchlist block → REVIEW terminal');
      this.handleReviewTerminal(notes, providerReturn);
      return;
    }

    // Tenta processar erro do Veriff Lite se notes ou providerReturn estiver presente
    const veriffError = this.processVeriffLiteError(notes, providerReturn);

    console.log('[KYC] handlePollingFailure - Veriff error result:', veriffError);

    if (veriffError) {
      console.log('[KYC] handlePollingFailure - Checking modal requirements:', {
        requiresModal: veriffError.requiresModal,
        validationType: veriffError.validationType,
        shouldOpenModal: veriffError.requiresModal && veriffError.validationType === 'legal_name_match'
      });

      // TC4 — SomosKycV1 name NOT similar (REVIEW_AND_RETRY): the SERPRO name is too
      // different to auto-suggest, so offer the "amend your details" form and restart
      // with a fresh KYC check, instead of the classifier fix modal.
      if (
        this.isSomosV1 &&
        this.ks.getNamesMismatchAction({ notes, provider_return: providerReturn } as any) === 'REVIEW_AND_RETRY'
      ) {
        console.log('[KYC] TC4 — REVIEW_AND_RETRY → amend details then restart');
        this.isProcessingIndeterminate = false;
        this.hasProcessingError = true;
        this.errorInStep = this.currentProcessingStep;
        this.currentError = veriffError;
        this.processing = ProcessingStatus.REJECTED;
        this.updateProcessingStep(this.currentProcessingStep, false, true);
        this.openAmendDetailsAndRestart(veriffError.data?.currentLegalName ?? null);
        return;
      }

      // 🔍 VERIFICAR SE PRECISA ABRIR MODAL DE LEGAL NAME FIX
      if (veriffError.requiresModal && veriffError.validationType === 'legal_name_match') {
        console.log('[KYC] ✅ Opening legal name fix modal due to legal_name_match error');

        // Parar spinner e marcar step como erro ANTES de abrir modal
        this.isProcessingIndeterminate = false;
        this.hasProcessingError = true;
        this.errorInStep = this.currentProcessingStep;
        this.currentError = veriffError;
        this.processing = ProcessingStatus.REJECTED;
        this.updateProcessingStep(this.currentProcessingStep, false, true);

        // Abrir modal e retornar
        this.openLegalNameFixModal(veriffError, providerReturn);
        //Inserir evento

        return; // NÃO continuar com fluxo de erro
      } else {
        console.log('[KYC] ❌ NOT opening modal - conditions not met');
      }

      // Se não abriu modal, usa erro do Veriff Lite
      this.currentError = veriffError;

      if (veriffError.validationType === 'cpf_validation') {
        // SomosKycV1: anchor the failure on the personal-data step so the amber action-needed
        // override below (after the generic error marking) lands on the right row.
        if (this.isSomosV1) {
          this.currentProcessingStep = 'PRE_PROCESSING';
        }
        this.loadCurrentPiiData();
      }
    } else {
      // Usa mensagem genérica
      this.currentError = {
        code: 'DOCUMENT_REJECTED',
        message,
        suggestedAction: this.translate.instant('KYC.ERROR.TRY_AGAIN_SUGGESTION'),
        canRetry: true,
      };
    }

    // Set processing error state instead of changing to ERROR step
    this.hasProcessingError = true;
    this.errorInStep = this.currentProcessingStep;
    this.processing = ProcessingStatus.REJECTED;
    this.isProcessingIndeterminate = false; // Parar spinner

    // Mark current step as failed
    this.updateProcessingStep(this.currentProcessingStep, false, true);

    // SomosKycV1: a registry decline is correctable inline, so present the personal-data step as
    // action-needed (amber) rather than a hard error while the CPF-correction form is shown.
    if (this.isSomosV1 && this.currentError?.validationType === 'cpf_validation') {
      this.markSomosPersonalDataActionNeeded();
    }

    // 🔄 Verificar se atingiu limite de retries IMEDIATAMENTE após falha
    // Isso evita mostrar botão "Try Again" quando não há mais tentativas
    this.checkRetryLimitAfterFailure();

    // Keep in PROCESSING step to show error inline (card)
    // this.steps = 'ERROR'; // Don't change to ERROR screen
    this.result = {
      status: 'REJECTED',
      checkId: this.checkId,
      error: this.currentError,
    };
  }

  /**
   * Verifica se o usuário atingiu o limite de retries após uma falha.
   * Se atingiu, vai direto para a tela RETRY_LIMIT_EXCEEDED sem precisar
   * que o usuário clique em "Try Again" desnecessariamente.
   */
  protected async checkRetryLimitAfterFailure(): Promise<void> {
    try {
      const userId = this.data.ref;
      if (!userId) return;

      const provider = this.data.provider || 'verifflite';
      const { retryCount } = await this.ks.getUserRetryCount(userId, provider);

      const finalMaxRetries = await this.getEffectiveMaxRetries(userId);

      // Atualiza contadores para exibição
      this.serverCurrentRetries = retryCount;
      this.serverMaxRetries = finalMaxRetries;

      console.log('[KYC] checkRetryLimitAfterFailure:', {
        currentRetries: retryCount,
        maxRetries: finalMaxRetries,
        hasReachedLimit: retryCount >= finalMaxRetries && finalMaxRetries !== Infinity,
      });

      // Se atingiu o limite, vai direto para tela de erro final
      if (retryCount >= finalMaxRetries && finalMaxRetries !== Infinity) {
        console.log('[KYC] User has reached retry limit after failure, redirecting to RETRY_LIMIT_EXCEEDED');

        this.currentError = {
          code: 'RETRY_LIMIT_EXCEEDED',
          message: this.translate.instant('KYC.ERROR.RETRY_LIMIT_EXCEEDED_MSG', { currentRetries: retryCount, maxRetries: finalMaxRetries }),
          suggestedAction: this.translate.instant('KYC.ERROR.CONTACT_SUPPORT_VERIFICATION'),
          canRetry: false,
          currentRetries: retryCount,
          maxRetries: finalMaxRetries,
        };

        this.steps = 'RETRY_LIMIT_EXCEEDED';
        this.parseAgentDiagnostics(this.data?.notes);
        this.loadClientDob();
      }
    } catch (error) {
      console.error('[KYC] Error checking retry limit after failure:', error);
      // Em caso de erro, continua com fluxo normal (mostra Try Again)
    }
  }

  /**
   * Processa erro de retry limit excedido do backend
   */
  protected processRetryLimitError(errorData: any): KycError {
    const currentRetries = errorData?.currentRetries || 0;
    const maxRetries = errorData?.maxRetries || 3;
    const userRole = errorData?.userRole || 'CLIENT';

    this.serverCurrentRetries = currentRetries;
    this.serverMaxRetries = maxRetries;
    this.userRole = userRole;

    return {
      code: 'RETRY_LIMIT_EXCEEDED',
      message: this.translate.instant('KYC.ERROR.RETRY_LIMIT_EXCEEDED_MSG', { currentRetries, maxRetries }),
      suggestedAction: this.translate.instant('KYC.ERROR.CONTACT_SUPPORT_VERIFICATION'),
      canRetry: false,
      currentRetries,
      maxRetries,
      userRole,
      technicalDetails: `User has exceeded retry limit: ${currentRetries}/${maxRetries}`,
      data: errorData,
    };
  }

  /**
   * Processa erro de usuário bloqueado do backend
   */
  protected processUserBlockedError(errorData: any): KycError {
    const reasonCode = errorData?.reasonCode;
    const blockReason = errorData?.blockReason || this.translate.instant('KYC.ERROR.PREVIOUS_FAILURE');
    const specificMessage = errorData?.specificMessage || this.translate.instant('KYC.ERROR.ACCOUNT_BLOCKED');

    return {
      code: 'USER_BLOCKED',
      message: specificMessage,
      suggestedAction: this.translate.instant('KYC.ERROR.CONTACT_SUPPORT_ASSISTANCE'),
      canRetry: false,
      technicalDetails: `User blocked. Reason: ${blockReason} (Code: ${reasonCode})`,
      data: errorData,
    };
  }

  /**
   * Helper para obter retry info formatada
   */
  protected getRetryInfoText(): string {
    if (this.serverCurrentRetries > 0 && this.serverMaxRetries > 0) {
      return `Attempts: ${this.serverCurrentRetries}/${this.serverMaxRetries}`;
    }
    return '';
  }

  /**
   * Helper para verificar se usuário atingiu limite de tentativas
   */
  protected hasReachedRetryLimit(): boolean {
    return this.serverCurrentRetries >= this.serverMaxRetries;
  }

  /**
   * Processa erros do Veriff Lite baseado nos notes ou provider_return
   * Retorna um KycError se encontrar um note declined, resubmission ou internal validation
   */
  protected processVeriffLiteError(notes?: any[], providerReturn?: any): KycError | null {
    // 🔧 Parse provider_return se vier como string
    let parsedProviderReturn = providerReturn;
    if (typeof providerReturn === 'string') {
      try {
        parsedProviderReturn = JSON.parse(providerReturn);
        console.log('[KYC] processVeriffLiteError - Parsed provider_return from string to object');
      } catch (error) {
        console.error('[KYC] processVeriffLiteError - Failed to parse provider_return string:', error);
        parsedProviderReturn = null;
      }
    }

    console.log('[KYC] processVeriffLiteError - Starting error processing', {
      notesCount: notes?.length,
      hasProviderReturn: !!parsedProviderReturn,
      providerReturnType: typeof parsedProviderReturn,
      providerReturnKeys: parsedProviderReturn ? Object.keys(parsedProviderReturn) : [],
      hasRegistryData: !!parsedProviderReturn?.registry_data,
      registryDataStatus: parsedProviderReturn?.registry_data?.status,
      registryDataCode: parsedProviderReturn?.registry_data?.code,
      hasVerification: !!parsedProviderReturn?.verification,
      verificationStatus: parsedProviderReturn?.verification?.status,
      verificationCode: parsedProviderReturn?.verification?.code,
      registryStatus: parsedProviderReturn?.registry_status,
    });

    // 🔍 Verificar primeiro se há erro direto no provider_return
    // Suporta tanto registry_data.status (estrutura legada) quanto verification.status + registry_status (estrutura atual)
    const isRegistryDeclined =
      parsedProviderReturn?.registry_data?.status === 'declined' ||
      parsedProviderReturn?.verification?.status === 'declined' ||
      parsedProviderReturn?.registry_status === 'declined';

    if (isRegistryDeclined) {
      // Normalizar dados vindos de qualquer das estruturas possíveis
      const registryDataLegacy = parsedProviderReturn?.registry_data;
      const verificationData = parsedProviderReturn?.verification;

      const resolvedCode = registryDataLegacy?.code ?? verificationData?.code;
      const resolvedReasonCode = registryDataLegacy?.reasonCode ?? verificationData?.reasonCode;
      const resolvedReason =
        registryDataLegacy?.reason ||
        parsedProviderReturn?.rejection_reason ||
        this.translate.instant('KYC.ERROR.UNABLE_VALIDATE_CPF');

      console.log('[KYC] ✅ processVeriffLiteError - Registry declined error found in provider_return', {
        source: registryDataLegacy ? 'registry_data' : 'verification',
        code: resolvedCode,
        reasonCode: resolvedReasonCode,
        reason: resolvedReason,
      });

      // Compatibilizar com o restante do bloco que usa `registryData`
      const registryData = {
        status: 'declined',
        code: resolvedCode,
        reasonCode: resolvedReasonCode,
        reason: resolvedReason,
      };
      const reasonCode = resolvedReasonCode;
      const reason = resolvedReason;

      if (this.isSomosV1) {
        // SomosKycV1: ANY registry/CPF decline → offer the "correct your details" form. The registry
        // validates CPF + name + DOB against the government (Serpro) records, so a decline
        // (reasonCode 542 or otherwise) means one of those is wrong or a typo — letting the user fix
        // the details and resubmit is the right next step. The retry-limit guard still bounds retries.
        console.log('[KYC] ✅ processVeriffLiteError - Registry/CPF decline → correction form', {
          code: registryData.code,
          reasonCode: reasonCode,
          reason: reason,
        });

        return {
          code: (reasonCode ? `KYC.VERIFF_REASON.${reasonCode}` : 'KYC.ERROR.UNABLE_VALIDATE_CPF') as any,
          message: reasonCode
            ? getVeriffReasonMessage(reasonCode, this.translate)
            : this.translate.instant('KYC.ERROR.UNABLE_VALIDATE_CPF'),
          suggestedAction: this.translate.instant('KYC.ERROR.VERIFY_CORRECT_INFO'),
          canRetry: true,
          shouldWait: false,
          requiresModal: false, // correction is inline, not a modal
          validationType: 'cpf_validation',
          technicalDetails: `Registry declined - Reason: ${reason} (Code: ${registryData.code}, ReasonCode: ${reasonCode})`,
          data: {
            source: 'registry_validation',
            registryData,
            errorCode: reasonCode,
            validationFailed: true,
          },
        };
      }

      // ── Non-SomosKycV1 (verifflite / veriff / …): exact pre-SomosKycV1 decline handling ──
      // Se o erro for relacionado a CPF (somente reasonCode 542)
      if (reasonCode === 542) {
        console.log('[KYC] ✅ processVeriffLiteError - CPF validation error detected (reasonCode 542)', {
          code: registryData.code,
          reasonCode: reasonCode,
          willOpenModal: false, // CPF error does NOT open modal
          willReturnError: true
        });

        return {
          code: 'KYC.VERIFF_REASON.542' as any,
          message: getVeriffReasonMessage(542, this.translate),
          suggestedAction: this.translate.instant('KYC.ERROR.VERIFY_CORRECT_INFO'),
          canRetry: true,
          shouldWait: false,
          requiresModal: false, // CPF error does NOT open modal
          validationType: 'cpf_validation', // Not legal_name_match
          technicalDetails: `Registry declined - Reason: ${reason} (Code: ${registryData.code}, ReasonCode: ${reasonCode})`,
          data: {
            source: 'registry_validation',
            registryData,
            errorCode: reasonCode,
            validationFailed: true
          },
        };
      }

      // Outros erros de registry
      // Consulta notes para determinar canRetry via action (ex: "try again")
      const matchingNote = Array.isArray(notes)
        ? notes.find((n) => n.data?.status === 'declined' && String(n.data?.declinedReason?.reasonCode) === String(reasonCode))
        : null;
      const noteAction = matchingNote?.data?.declinedReason?.action;
      const canRetryFromNote = noteAction === 'try again';

      console.log('[KYC] ⚠️ processVeriffLiteError - Other registry error (not CPF)', {
        code: registryData.code,
        reasonCode: reasonCode,
        reason: reason,
        noteAction,
        canRetry: canRetryFromNote,
      });

      return {
        code: `KYC.VERIFF_REASON.${reasonCode}` as any,
        message: getVeriffReasonMessage(reasonCode, this.translate),
        suggestedAction: canRetryFromNote
          ? this.translate.instant('KYC.VERIFF_REASON.ACTION_TRY_AGAIN')
          : this.translate.instant('KYC.ERROR.CONTACT_SUPPORT_ASSISTANCE'),
        canRetry: canRetryFromNote,
        technicalDetails: `Registry declined - ${reason} (ReasonCode: ${reasonCode})`,
        data: {
          source: 'registry_validation',
          registryData,
          errorCode: reasonCode
        },
      };
    } else {
      console.log('[KYC] ❌ processVeriffLiteError - No registry error in provider_return', {
        hasProviderReturn: !!parsedProviderReturn,
        hasRegistryData: !!parsedProviderReturn?.registry_data,
        registryDataStatus: parsedProviderReturn?.registry_data?.status || 'N/A',
        verificationStatus: parsedProviderReturn?.verification?.status || 'N/A',
        registryStatus: parsedProviderReturn?.registry_status || 'N/A',
      });
    }

    if (!Array.isArray(notes) || notes.length === 0) {
      console.log('[KYC] ❌ processVeriffLiteError - No notes to process');
      return null;
    }

    console.log('[KYC] processVeriffLiteError - All notes:', JSON.stringify(notes, null, 2));

    // Busca o último note de erro (reversed para pegar o mais recente)
    const errorNote = [...notes]
      .reverse()
      .find((n) =>
        // Internal validation failed (pode vir com source ou validationFailed)
        (n.data?.source === 'internal_validation' && n.data?.errorCode) ||
        (n.data?.validationFailed && n.data?.errorCode) ||
        // Declined com reasonCode
        (n.data?.status === 'declined' && n.data?.declinedReason?.reasonCode) ||
        // Resubmission requested com reasonCode
        (n.data?.status === 'resubmission_requested' && n.data?.declinedReason?.reasonCode)
      );

    console.log('[KYC] processVeriffLiteError - Error note found:', errorNote ? JSON.stringify(errorNote, null, 2) : 'null');

    if (!errorNote) {
      console.log('[KYC] processVeriffLiteError - No error note matching criteria');
      return null;
    }

    // 🔍 INTERNAL VALIDATION: Processar erro de validação interna
    // Pode vir com source='internal_validation' OU validationFailed=true
    if ((errorNote.data?.source === 'internal_validation' && errorNote.data?.errorCode) ||
        (errorNote.data?.validationFailed && errorNote.data?.errorCode)) {
      const errorCode = errorNote.data.errorCode;
      // Tenta pegar validationType de múltiplas fontes
      const validationType = errorNote.data.validation ||
                            errorNote.data?.declinedReason?.validation ||
                            errorNote.data?.declinedReason?.reasonCode;
      const reason = errorNote.data.reason || errorNote.message;

      console.log('[KYC] processVeriffLiteError - Internal validation error detected:', {
        errorCode,
        validationType,
        reason,
        declinedReasonCode: errorNote.data?.declinedReason?.reasonCode,
        declinedReasonType: errorNote.data?.declinedReason?.type,
        fullData: errorNote.data
      });

      // Verificar se é erro de NAME MISMATCH especificamente
      // Só abre modal para erros relacionados ao nome divergente
      const isNameMismatchError = errorCode === 'KYC_NAME_MISMATCH' ||
                                  errorCode === 'KYC_NAME_VALIDATION_FAILED' ||
                                  validationType === 'legal_name_match' ||
                                  errorNote.data?.declinedReason?.reasonCode === 'legal_name_match';

      console.log('[KYC] processVeriffLiteError - Is NAME_MISMATCH error?', {
        isNameMismatchError,
        errorCode,
        validationType,
        shouldOpenModal: isNameMismatchError
      });

      if (isNameMismatchError) {
        console.log('[KYC] processVeriffLiteError - Processing NAME_MISMATCH error');
        // Mensagens específicas para NAME_MISMATCH
        const errorMessage = this.translate.instant('KYC_NAME_VALIDATION_FAILED');
        const actionMessage = this.translate.instant('KYC_NAME_VALIDATION_FAILED_ACTION');

        const nameError = {
          code: 'LEGAL_NAME_MISMATCH' as any, // Código simples, não chave de tradução
          message: errorMessage, // Já traduzido
          suggestedAction: actionMessage, // Já traduzido
          canRetry: true,
          requiresModal: true, // Flag especial para abrir o modal
          validationType: 'legal_name_match',
          technicalDetails: `Internal Validation Failed: ${validationType} - ${reason}`,
          data: errorNote,
        };

        console.log('[KYC] processVeriffLiteError - Returning NAME_MISMATCH error with modal:', nameError);
        return nameError;
      }

      // 🔒 IDENTITY CROSS-VALIDATION (check CANCELLED): today this only means the CPF read from
      // the document photo didn't match the registered CPF — usually a poor-quality photo
      // (CPK-4310). Route to the correction card with a specific message: the user reviews the
      // registered details and resubmits a clearer photo (unchanged details skip /profile).
      const isIdentityCrossValidation =
        validationType === 'identity_cross_validation_failed' ||
        errorNote.data?.declinedReason?.reasonCode === 'identity_cross_validation_failed';

      if (isIdentityCrossValidation) {
        console.log('[KYC] processVeriffLiteError - Identity cross-validation (document CPF mismatch) detected');
        return {
          code: 'KYC_DOCUMENT_CPF_MISMATCH' as any,
          message: this.translate.instant('KycDocumentCpfMismatchMessage'),
          suggestedAction: this.translate.instant('KycDocumentCpfMismatchAction'),
          canRetry: true,
          requiresModal: false,
          validationType: 'cpf_validation',
          technicalDetails: `Internal Validation Failed: ${validationType} - ${reason}`,
          data: errorNote,
        };
      }

      // Outros erros de internal validation NÃO abrem modal
      console.log('[KYC] processVeriffLiteError - Other internal validation error (not NAME_MISMATCH):', {
        errorCode,
        willOpenModal: false
      });

      // Obter mensagem traduzida do código de erro para outros tipos
      const errorMessage = this.translate.instant(errorCode) !== errorCode
        ? this.translate.instant(errorCode)
        : this.translate.instant('KYC.ERROR.INTERNAL_VALIDATION_FAILED');

      const suggestedAction = this.translate.instant(`${errorCode}_ACTION`) !== `${errorCode}_ACTION`
        ? this.translate.instant(`${errorCode}_ACTION`)
        : this.translate.instant('KYC.ERROR.CONTACT_SUPPORT');

      return {
        code: errorCode as any,
        message: errorMessage,
        suggestedAction: suggestedAction,
        canRetry: true, // TODOS os internal validation errors permitem retry
        requiresModal: false, // NÃO abre modal
        technicalDetails: `Internal Validation Failed: ${validationType} - ${reason}`,
        data: errorNote,
      };
    }

    // 🔄 RESUBMISSION / DECLINED: Processar erro do provider
    const reasonCode = errorNote.data.declinedReason.reasonCode;
    const action = errorNote.data.declinedReason.action;
    const status = errorNote.data.status; // 'declined' ou 'resubmission_requested'

    // Obtém mensagem traduzida do reasonCode
    const errorMessage = getVeriffReasonMessage(reasonCode, this.translate);

    // Define ação sugerida e canRetry baseado no action e status
    let suggestedAction = '';
    let canRetry = false;

    if (status === 'resubmission_requested') {
      // Resubmission sempre permite retry
      suggestedAction = this.translate.instant('KYC.VERIFF_REASON.ACTION_RESUBMIT') ||
                        this.translate.instant('KYC.VERIFF_REASON.ACTION_TRY_AGAIN');
      canRetry = true;
    } else if (action === 'try again') {
      suggestedAction = this.translate.instant('KYC.VERIFF_REASON.ACTION_TRY_AGAIN');
      canRetry = true;
    } else if (action === 'blocked') {
      suggestedAction = this.translate.instant('KYC.VERIFF_REASON.ACTION_BLOCKED');
      canRetry = false;
    } else {
      suggestedAction = this.translate.instant('KYC.ERROR.CONTACT_SUPPORT');
      canRetry = false;
    }

    return {
      code: `KYC.VERIFF_REASON.${reasonCode}` as any,
      message: errorMessage,
      suggestedAction,
      canRetry,
      technicalDetails: `${status === 'resubmission_requested' ? 'Resubmission Requested' : 'Declined'} - Veriff Reason Code: ${reasonCode}`,
      data: errorNote,
    };
  }

  // ==================== WAIT TIMER METHODS ====================

  /**
   * Inicia timer de espera de 2 minutos para erros com shouldWait=true
   */
  protected startWaitTimer(): void {
    this.waitTimeRemaining = 120; // 2 minutes in seconds

    this.waitTimer = setInterval(() => {
      this.waitTimeRemaining--;

      if (this.waitTimeRemaining <= 0) {
        clearInterval(this.waitTimer);
        this.waitTimer = undefined;
      }
    }, 1000);
  }

  /**
   * Fallback para notas no formato antigo (Veriff standard)
   * Estrutura: n.action === 'declined', n.nextAction, n.description, n.reasonCode
   */
  protected processLegacyNoteError(notes?: any[]): KycError | null {
    if (!Array.isArray(notes) || notes.length === 0) return null;

    const declinedNote = [...notes].reverse().find((n) => n.action === 'declined');
    if (!declinedNote) return null;

    let suggestedAction = '';
    let canRetry = false;

    if (declinedNote.nextAction === 'try_again') {
      suggestedAction = this.translate.instant('KYC.VERIFF_REASON.ACTION_TRY_AGAIN');
      canRetry = true;
    } else if (declinedNote.nextAction === 'blocked') {
      suggestedAction = this.translate.instant('KYC.VERIFF_REASON.ACTION_BLOCKED');
      canRetry = false;
    } else {
      suggestedAction = this.translate.instant('KYC.ERROR.CONTACT_SUPPORT');
      canRetry = false;
    }

    return {
      code: 'DOCUMENT_REJECTED' as import('./kyc-upload.types').KycErrorCode,
      message: declinedNote.description || this.translate.instant('KYC.ERROR.DOCUMENT_REJECTED'),
      suggestedAction,
      canRetry,
      technicalDetails: declinedNote.reasonCode ? `Reason: ${declinedNote.reasonCode}` : undefined,
      data: declinedNote,
    };
  }

  /**
   * Verifica se ainda está em período de espera
   */
  protected isWaitTimeActive(): boolean {
    return this.waitTimeRemaining > 0;
  }

  /**
   * Formata o tempo restante em MM:SS
   */
  protected formatWaitTime(): string {
    const minutes = Math.floor(this.waitTimeRemaining / 60);
    const seconds = this.waitTimeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // ==================== ERROR MESSAGE METHODS ====================

  /**
   * Obtém a mensagem de erro traduzida baseada no código do erro
   */
  getErrorMessage(errorCode?: string): string {
    if (!errorCode) {
      return 'KYC_UNHANDLED_ERROR';
    }
    // Retorna o código que será usado como chave de tradução
    return errorCode;
  }

  /**
   * Obtém o texto da mensagem de erro atual para exibição
   */
  getCurrentErrorMessage(): string {
    if (!this.currentError || !this.currentError.code) {
      return this.getErrorMessage('KYC_UNHANDLED_ERROR');
    }

    return this.getErrorMessage(this.currentError.code);
  }

  protected buildBackendError(errorData: any): KycError {
    const code = errorData.code || 'UNKNOWN_ERROR';
    const translatedCode = this.translate.instant(code);
    const neutralMessage = translatedCode !== code
      ? translatedCode
      : this.translate.instant('KYC_UNHANDLED_ERROR');

    return {
      code,
      message: this.isAgentSession ? (errorData.message || 'An error occurred') : neutralMessage,
      userMessage: errorData.userMessage,
      suggestedAction: errorData.actionRequired || errorData.suggestedAction || 'Please try again',
      canRetry: errorData.canRetry !== false,
      shouldWait: errorData.shouldWait || false,
      type: errorData.type,
      requestId: errorData.requestId,
      data: errorData.data,
      technicalDetails: errorData.message || JSON.stringify(errorData, null, 2)
    };
  }

  /**
   * Fetches the case email from the claim details
   */
  protected async fetchCaseEmail(): Promise<void> {
    try {
      if (!this.data.claim_id) return;

      const claimWithCase = await this.claimsService.getClaimWithCase(this.data.claim_id);
      if (claimWithCase && claimWithCase.cp_case && claimWithCase.cp_case.attributes) {
        this.caseEmail = claimWithCase.cp_case.attributes.email || null;
        console.log('[KYC] Case email fetched:', this.caseEmail);
      }
    } catch (error) {
      console.error('[KYC] Error fetching case email:', error);
    }
  }

  /**
   * Handles photo button click event from input-upload-file
   * Sends KYC_DOC_SOURCE_PHOTO event (non-blocking)
   */
  onKycPhotoButtonClick(): void {
    if (this.cachedCaseId) {
      // Fast path: use cached caseId
      this.eventService.pushNew(EventTypes.KYC_DOC_SOURCE_PHOTO, { caseId: this.cachedCaseId }).catch(error => {
        console.error('[KYC] Photo button event not sent:', error);
      });
    } else {
      // Fallback: use claimId (async lookup)
      this.eventService.sendEventWithCaseId(EventTypes.KYC_DOC_SOURCE_PHOTO, this.data.claim_id).catch(error => {
        console.error('[KYC] Photo button event not sent:', error);
      });
    }
  }

  /**
   * Handles upload button click event from input-upload-file
   * Sends KYC_DOC_SOURCE_LOAD event (non-blocking)
   */
  onKycUploadButtonClick(): void {
    if (this.cachedCaseId) {
      // Fast path: use cached caseId
      this.eventService.pushNew(EventTypes.KYC_DOC_SOURCE_LOAD, { caseId: this.cachedCaseId }).catch(error => {
        console.error('[KYC] Upload button event not sent:', error);
      });
    } else {
      // Fallback: use claimId (async lookup)
      this.eventService.sendEventWithCaseId(EventTypes.KYC_DOC_SOURCE_LOAD, this.data.claim_id).catch(error => {
        console.error('[KYC] Upload button event not sent:', error);
      });
    }
  }

  /**
   * Grava evento quando o tipo de CNH é selecionado (física ou digital)
   * O evento é gravado sempre que o método é chamado, mesmo que a opção já esteja selecionada
   */
  onCnhTypeChange(newType: 'digital' | 'physical'): void {
    this.cnhType = newType;
    this.trackCnhTypeSelection(newType);
  }

  /**
   * Método privado para gravar o evento de seleção de CNH
   */
  private trackCnhTypeSelection(cnhType: 'digital' | 'physical'): void {
    const eventType = cnhType === 'digital'
      ? EventTypes.DIGITAL_CNH_SELECTED
      : EventTypes.PHYSICAL_CNH_SELECTED;

    if (this.cachedCaseId) {
      // Fast path: use cached caseId
      this.eventService.pushNew(eventType, { caseId: this.cachedCaseId, cnhType }).catch(error => {
        console.error('[KYC] CNH type selection event not sent:', error);
      });
    } else if (this.data.claim_id) {
      // Fallback: use claimId (async lookup)
      this.eventService.sendEventWithCaseId(eventType, this.data.claim_id).catch(error => {
        console.error('[KYC] CNH type selection event not sent:', error);
      });
    } else {
      // Se não tiver claim_id, tenta gravar sem caseId
      this.eventService.pushNew(eventType, { cnhType }).catch(error => {
        console.error('[KYC] CNH type selection event not sent:', error);
      });
    }
  }

  // ==================== PEP/SANCTIONS VERIFICATION ====================

  /**
   * Verifica se existe erro de PEP/Sanctions nas notas
   * Retorna true se encontrar erro relacionado a PEP ou Sanctions
   */
  protected checkPepSanctionsError(notes?: any[]): boolean {
    if (!Array.isArray(notes) || notes.length === 0) {
      return false;
    }

    console.log('[KYC] checkPepSanctionsError - Checking notes for PEP/Sanctions errors');

    // Busca note com erro de PEP ou Sanctions
    const pepSanctionsNote = notes.find((note) => {
      const errorCode = note.data?.errorCode;
      const reasonCode = note.data?.declinedReason?.reasonCode;
      const registryCode = note.data?.registry_data?.reasonCode;
      const webhookReason = note.data?.webhookReason;
      const isBlocked = note.data?.isBlocked;

      // Verifica múltiplos indicadores de PEP/Sanctions
      const isPepError =
        errorCode === 'KYC_PEP_DETECTED' ||
        errorCode === 'KYC_SANCTIONS_DETECTED' ||
        errorCode === 'KYC_WATCHLIST_DETECTED' ||
        reasonCode === 'pep_detected' ||
        reasonCode === 'sanctions_match' ||
        reasonCode === 'watchlist_match' ||
        reasonCode === '127' || // ✅ Veriff Lite: Face match with blocklist (PEP/Sanctions)
        registryCode === 543 || // PEP detected
        registryCode === 544 || // Sanctions match
        note.data?.isPep === true ||
        note.data?.isSanctioned === true ||
        note.data?.isWatchlist === true ||
        // ✅ Verifica webhookReason para Veriff Lite
        (webhookReason && webhookReason.toLowerCase().includes('sanction')) ||
        (webhookReason && webhookReason.toLowerCase().includes('pep')) ||
        (webhookReason && webhookReason.toLowerCase().includes('watchlist')) ||
        // ✅ Verifica se há watchlistScreening com hits
        (note.data?.watchlistScreening?.totalHits > 0) ||
        // ✅ Verifica combinação de isBlocked + reasonCode 127
        (isBlocked && reasonCode === '127');

      if (isPepError) {
        console.log('[KYC] checkPepSanctionsError - PEP/Sanctions error found:', {
          errorCode,
          reasonCode,
          registryCode,
          webhookReason,
          isBlocked,
          watchlistHits: note.data?.watchlistScreening?.totalHits,
          noteData: note.data
        });
      }

      return isPepError;
    });

    return !!pepSanctionsNote;
  }

  // ==================== AGENT DIAGNOSTIC METHODS ====================

  /**
   * Parses KYC notes into a structured diagnostics object for agent display.
   * Only populated when isAgentSession is true.
   */
  protected parseAgentDiagnostics(notes?: any[]): void {
    if (!this.isAgentSession) return;

    const lastNote = Array.isArray(notes) && notes.length > 0
      ? notes[notes.length - 1]
      : null;

    const pr = this.data.provider_return;

    // errorCode: from notes first, then provider_return fallback
    const errorCode = lastNote?.data?.errorCode
      || pr?.verification?.reason
      || pr?.rejection_reason
      || 'N/A';

    // reasonCode: from notes first, then provider_return fallback
    const reasonCode = lastNote?.data?.declinedReason?.reasonCode
      || lastNote?.data?.registry_data?.reasonCode
      || pr?.verification?.reasonCode
      || 'N/A';

    // PEP/Sanctions: null when registry was declined before PEP was ever checked
    const registryDeclined = pr?.registry_status === 'declined'
      || pr?.audit?.should_not_have_document_data === true;
    const pepSanctions = registryDeclined ? null : this.checkPepSanctionsError(notes);

    this.agentDiagnostics = {
      errorCode,
      reasonCode,
      provider: lastNote?.data?.provider || this.data.provider || 'N/A',
      retries: `${this.serverCurrentRetries} / ${this.serverMaxRetries}`,
      pepSanctions,
      notesCount: notes?.length || 0,
      rawNotes: JSON.stringify(notes, null, 2),
    };
  }

  /**
   * Agent action: increase max retries by +1 via backend.
   */
  async onAgentIncreaseRetries(): Promise<void> {
    const userId = this.data.ref;
    if (!userId || this.agentActionLoading) return;

    this.agentActionLoading = 'retries';
    try {
      const result = await this.ks.agentIncreaseRetries(userId);
      this.serverMaxRetries = result.newMax;
      if (this.agentDiagnostics) {
        this.agentDiagnostics.retries = `${this.serverCurrentRetries} / ${result.newMax}`;
      }
      alert(this.translate.instant('KYC.AGENT_ACTIONS.SUCCESS_RETRIES', result));

      // Se o limite não é mais atingido, resetar para permitir nova tentativa
      if (this.serverCurrentRetries < result.newMax) {
        this.currentError = null;
        this.hasProcessingError = false;
        this.errorInStep = null;
        this.initializeProcessingSteps();
        this.steps = 'UPLOAD';
      }
    } catch (err) {
      console.error('[KYC] Agent increase retries failed:', err);
      alert(this.translate.instant('KYC.AGENT_ACTIONS.ERROR_GENERIC'));
    } finally {
      this.agentActionLoading = null;
    }
  }

  /**
   * Agent action: bypass KYC entirely via backend.
   */
  async onAgentBypassKyc(): Promise<void> {
    await this.openSkipKycModal();
  }

  /**
   * Opens the agent skip-KYC modal (CPK-3338). Persists a SKIPPED_BY_AGENT
   * cp_kyc_check carrying the IC_AGENT_FLOW so signature prerequisites are
   * unblocked. `defaultReason` pre-selects an option when the calling context
   * already implies one (e.g. underage client).
   */
  private async openSkipKycModal(defaultReason?: KycSkipReason): Promise<void> {
    const userId = this.data.ref;
    if (!userId || this.agentActionLoading) return;

    this.agentActionLoading = 'bypass';
    try {
      const ref = this.dialog.open<
        AgentSkipKycModalComponent,
        AgentSkipKycDialogData,
        AgentSkipKycDialogResult
      >(AgentSkipKycModalComponent, {
        data: {
          userId,
          actionId: this.data.action_id,
          defaultReason,
        },
        width: '520px',
        autoFocus: false,
      });

      const result = await ref.afterClosed().toPromise();
      if (result?.saved) {
        this.handlePollingSuccess();
      }
    } finally {
      this.agentActionLoading = null;
    }
  }

  // ==================== AGENT REVIEW METHODS ====================

  /**
   * Verifica se existe erro de agent_review_required nas notas
   * e se é o ÚNICO erro presente (priorização)
   */
  protected checkAgentReviewRequired(notes?: any[]): {
    hasAgentReview: boolean;
    isOnlyError: boolean;
    otherErrors: any[];
  } {
    if (!notes || notes.length === 0) {
      return { hasAgentReview: false, isOnlyError: false, otherErrors: [] };
    }

    let agentReviewNote = null;
    const otherErrors: any[] = [];

    for (const note of notes) {
      const isAgentReview =
        note.data?.errorCode === 'KYC_AGENT_REVIEW_REQUIRED' ||
        note.data?.declinedReason?.reasonCode === 'agent_review_required';

      if (isAgentReview) {
        agentReviewNote = note;
      } else {
        // Verifica se é um erro (declined, resubmission, etc)
        const isError =
          note.data?.status === 'declined' ||
          note.data?.status === 'resubmission_requested' ||
          (note.data?.declinedReason && note.data.declinedReason.reasonCode !== 'agent_review_required');

        if (isError) {
          otherErrors.push(note);
        }
      }
    }

    return {
      hasAgentReview: !!agentReviewNote,
      isOnlyError: !!agentReviewNote && otherErrors.length === 0,
      otherErrors,
    };
  }

  /**
   * Processa o erro de agent_review_required e prepara dados para modal de confirmação
   */
  protected async processAgentReviewError(
    notes: any[],
    kycCheckId?: string
  ): Promise<void> {
    console.log('[KYC] processAgentReviewError - Called with:', {
      notesCount: notes?.length,
      kycCheckId,
      actionDoneActionId: this.actionDoneActionId,
      checkId: this.checkId
    });

    try {
      // Usa o ID passado ou fallback para actionDoneActionId/checkId
      const effectiveKycCheckId = kycCheckId || this.actionDoneActionId || this.checkId;

      if (!effectiveKycCheckId) {
        console.error('[KYC] No KYC check ID available for agent review');
        throw new Error('No KYC check ID available');
      }

      console.log('[KYC] Using effective KYC check ID:', effectiveKycCheckId);

      // Extrai dados do provider_return se disponível
      let extractedData: any = null;

      const kycCheckData = await this.fetchKycCheckData(effectiveKycCheckId);
      if (kycCheckData?.provider_return) {
        extractedData = this.extractDocumentData(kycCheckData.provider_return);
      }

      console.log('[KYC] Extracted data for agent review:', extractedData);

      // Abre o modal de confirmação
      this.openAgentReviewConfirmationModal(effectiveKycCheckId, extractedData);

    } catch (error) {
      console.error('Error processing agent review:', error);
      // Se der erro, mostra erro genérico
      this.currentError = {
        code: 'UNKNOWN_ERROR',
        message: 'Failed to load review data',
        suggestedAction: 'Please try again or contact support',
        canRetry: true,
      };
      this.steps = 'ERROR';
    }
  }

  /**
   * Extrai dados relevantes do document do provider_return
   */
  protected extractDocumentData(providerReturn: any): any {
    const extracted: any = {};

    try {
      // Tenta extrair de verification primeiro (estrutura do Veriff)
      let person = providerReturn?.verification?.person;
      let document = providerReturn?.verification?.document;

      // Fallback para document_data_extracted
      if (!person && providerReturn?.document_data_extracted?.person) {
        person = providerReturn.document_data_extracted.person;
      }
      if (!document && providerReturn?.document_data_extracted?.document) {
        document = providerReturn.document_data_extracted.document;
      }

      // Extrai dados do person
      if (person) {
        // Para Veriff, firstName contém o nome completo
        extracted.fullName = person.firstName ||
                            `${person.firstName || ''} ${person.lastName || ''}`.trim();
        extracted.dateOfBirth = person.dateOfBirth;

        // CPF/ID Number
        extracted.cpf = person.idNumber;

        // Outros dados
        extracted.nationality = person.nationality;
      }

      // Extrai dados do document
      if (document) {
        extracted.documentType = document.type;
        extracted.documentNumber = document.number;
        extracted.documentCountry = document.country;
        extracted.validFrom = document.validFrom;
        extracted.validUntil = document.validUntil;
      }

      // Extrai status da verificação
      if (providerReturn?.verification) {
        extracted.verificationStatus = providerReturn.verification.status;
        extracted.verificationCode = providerReturn.verification.code;
      }

      console.log('[KYC] Extracted document data:', extracted);

    } catch (error) {
      console.error('Error extracting document data:', error);
    }

    return extracted;
  }

  /**
   * Abre o modal de confirmação de revisão do agente
   */
  protected openAgentReviewConfirmationModal(kycCheckId: string, extractedData: any): void {
    console.log('[KYC] openAgentReviewConfirmationModal - Opening modal with:', {
      kycCheckId,
      extractedData
    });

    const dialogRef = this.dialog.open(AgentReviewConfirmationModalComponent, {
      height: '90vh',
      maxHeight: '700px',
      width: '90vw',
      maxWidth: '700px',
      disableClose: true,
      data: {
        kycCheckId,
        extractedData
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        console.log('[KYC] Agent review confirmation modal closed with result:', result);

        if (result?.revalidated && result?.passed === true) {
          // Revalidação passou! Mostrar tela de sucesso
          console.log('[Agent Review] ✅ Revalidation PASSED - Showing success');
          this.hasProcessingError = false;
          this.currentError = null;
          this.processing = ProcessingStatus.APPROVED;
          this.steps = 'SUCCESS';
          this.result = {
            status: 'VERIFIED',
            checkId: this.checkId,
            message: result.message || this.translate.instant('identityVerified'),
          };
        } else if (result?.revalidated && result?.passed === false) {
          // Revalidação falhou, voltar para upload
          console.log('[Agent Review] ❌ Revalidation FAILED - Back to upload');
          this.hasProcessingError = false;
          this.currentError = null;

          // Se tiver arquivos, volta para upload para retry
          if (this.frontImageFile) {
            console.log('[Agent Review] Files exist, going back to upload');
            this.steps = 'UPLOAD';
          } else {
            this.steps = 'UPLOAD';
          }
        } else if (result?.confirmed && !result?.revalidated) {
          // Usuário confirmou mas ainda não revalidou (pode acontecer se houver erro)
          console.log('[Agent Review] ⚠️ Confirmed but not revalidated (error case)');
          // Voltar para upload
          this.steps = 'UPLOAD';
        } else {
          // Usuário cancelou ou fechou o modal
          console.log('[Agent Review] ❌ Modal cancelled/closed');
          this.steps = 'UPLOAD';
        }
      });
  }

  /**
   * Navigates to FAQ page
   */
  goToFaq(): void {
    this.router.navigate(['/client/faq']);
    this.close();
  }

  /**
   * Navega para a página de contato com suporte
   */
  contactSupport() {
    // Check if max retries exceeded and we have a case email
    if (this.steps === 'RETRY_LIMIT_EXCEEDED' && this.caseEmail) {
      window.location.href = `mailto:${this.caseEmail}`;
      return;
    }

    // Tenta pegar claim_id de várias fontes
    let claimId = this.data.claim_id;

    console.log('[KYC] Contact Support - claim_id from data:', claimId);

    // Se não encontrou no data, tenta pegar da URL
    if (!claimId) {
      // Primeiro tenta pegar diretamente do param 'claimId'
      claimId = this.activatedRoute.snapshot.paramMap.get('claimId') || undefined;
      console.log('[KYC] Contact Support - claim_id from claimId param:', claimId);

      // Se não encontrou, tenta pegar do param 'id' (usado em /claim/:id)
      if (!claimId) {
        claimId = this.activatedRoute.snapshot.paramMap.get('id') || undefined;
        console.log('[KYC] Contact Support - claim_id from id param:', claimId);
      }

      // Se ainda não encontrou, tenta buscar nos parent routes
      if (!claimId) {
        let route = this.activatedRoute.parent;
        while (route && !claimId) {
          claimId = route.snapshot.paramMap.get('claimId') ||
            route.snapshot.paramMap.get('id') ||
            undefined;
          console.log('[KYC] Contact Support - checking parent route, claim_id:', claimId);
          route = route.parent;
        }
      }
    }

    console.log('[KYC] Contact Support - final claim_id:', claimId);

    if (claimId) {
      this.router.navigate(['/client/messages/new', claimId]);
      this.close();
    } else {
      console.error('[KYC] Cannot contact support: claim_id not available');
      // Tenta navegar para a página de mensagens sem claim_id
      this.router.navigate(['/client/messages']);
      this.close();
    }
  }

  close() {
    const value = JSON.stringify(this.result);
    if (this.data.isComponentView) {
      this.completed.emit(value);
    } else {
      this.dialogRef.close(value);
    }
  }

  /**
   * Guarded close for accidental mid-flow dismissal (backdrop / ESC / header X).
   *
   * On a working (non-terminal) step we ask the user to confirm before discarding, because
   * closing loses in-flight capture/upload progress. On terminal steps (SUCCESS, ERROR,
   * END_PROCESSING, RETRY_LIMIT_EXCEEDED, BLOCKED) there is nothing to lose, so we close
   * immediately. Embedded (`isComponentView`) and non-`disableClose` dialogs also close
   * immediately, preserving existing behaviour for providers/openers that don't opt in.
   */
  confirmClose(): void {
    const terminalSteps: KycStep[] = [
      'SUCCESS',
      'ERROR',
      'END_PROCESSING',
      'RETRY_LIMIT_EXCEEDED',
      'BLOCKED',
    ];

    // Only guard when this dialog opted into `disableClose` (i.e. the capture flow) and we're
    // mid-flow. Everything else keeps the immediate-close behaviour it had before.
    if (
      this.data.isComponentView ||
      !this.dialogRef.disableClose ||
      terminalSteps.includes(this.steps)
    ) {
      this.close();
      return;
    }

    const ref = this.dialog.open(ConfirmModalComponent, {
      width: '90vw',
      maxWidth: '420px',
      data: {
        titleIcon: 'warning',
        title: 'KycDiscardVerificationTitle',
        message: 'KycDiscardVerificationMessage',
        confirmText: 'KycDiscardVerificationConfirm',
        cancelButton: 'KycDiscardVerificationCancel',
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean | null) => {
        if (confirmed === true) this.close();
      });
  }

  /**
   * Abre o modal de correção de nome legal com dados do erro
   */
  protected openLegalNameFixModal(error: KycError, providerReturn?: any): void {
    console.log('[KYC] openLegalNameFixModal - Called with error:', error);

    // Extrair dados do errorNote se disponível
    const errorData = error.data?.data || {};
    const declinedReason = errorData.declinedReason || {};
    let details = declinedReason.details || {};

    // 🔧 FIX: Extrair dados de namesMismatch se disponível
    const namesMismatch = errorData.namesMismatch || {};
    console.log('[KYC] openLegalNameFixModal - namesMismatch data:', namesMismatch);

    // Se faltar dados em details, tentar extrair de providerReturn
    if ((!details.veriffDob || !details.veriffIdNumber) && providerReturn) {
      console.log('[KYC] openLegalNameFixModal - Details missing, trying to extract from providerReturn');
      const person = providerReturn.verification?.person || providerReturn.document_data_extracted?.person;

      if (person) {
        // Veriff Lite often puts full name in firstName and null in lastName
        const fullName = person.firstName + (person.lastName ? ' ' + person.lastName : '');

        details = {
          ...details,
          veriffFullName: details.veriffFullName || fullName,
          veriffDob: details.veriffDob || person.dateOfBirth,
          veriffIdNumber: details.veriffIdNumber || person.idNumber
        };
        console.log('[KYC] openLegalNameFixModal - Extracted details from providerReturn:', details);
      }
    }

    // 🔧 FIX: Tentar obter KYC check ID de múltiplas fontes
    // Priority: actionDoneActionId > checkId
    // NOTE: providerReturn.verification.id é ID do Veriff, NÃO do banco!
    let effectiveKycCheckId = this.actionDoneActionId || this.checkId;

    console.log('[KYC] openLegalNameFixModal - ID resolution:', {
      actionDoneActionId: this.actionDoneActionId,
      checkId: this.checkId,
      veriffId: providerReturn?.verification?.id,
      effectiveKycCheckId,
      willHaveIdForRevalidation: !!effectiveKycCheckId
    });

    if (!effectiveKycCheckId) {
      console.warn('[KYC] ⚠️ No kyc_check ID available! Modal opened before KYC check was created in DB.');
      console.warn('[KYC] This will cause revalidation to fail with 404.');
      console.warn('[KYC] providerReturn.verification.id is Veriff ID, not DB ID - cannot use it.');
    }

    console.log('[KYC] openLegalNameFixModal - Extracted data:', {
      errorData,
      declinedReason,
      details,
      namesMismatch,
      userId: this.data.ref,
      checkId: this.checkId,
      actionDoneActionId: this.actionDoneActionId,
      effectiveKycCheckId
    });

    // Store mismatch classification for later use in UPLOAD screen
    this.storedMismatchCategory = namesMismatch.category as any;
    this.storedMismatchSubCategory = namesMismatch.subCategory as any;

    const dialogRef = this.dialog.open(LegalNameFixModalComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        userId: this.data.ref,
        currentLegalName: details.legalName || '',
        veriffName: details.veriffFullName || '',
        kycCheckId: effectiveKycCheckId || '',
        doneActionId: effectiveKycCheckId,
        issues: declinedReason.reasons || (declinedReason.type ? [declinedReason.type] : []),
        documentData: {
          legalName: details.veriffFullName,
          dateOfBirth: details.veriffDob,
          idNumber: details.veriffIdNumber
        },
        // 🔧 FIX: Passar dados de namesMismatch para o modal
        mismatchAction: namesMismatch.action,
        mismatchActions: namesMismatch.actions,
        actionLogic: namesMismatch.actionLogic,
        category: namesMismatch.category,
        subCategory: namesMismatch.subCategory,
        suggestedCorrection: namesMismatch.suggestedCorrection
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        console.log('[KYC] Legal name fix modal closed with result:', result);

        if (result?.updated) {
        if (result?.revalidated && result?.passed === true) {
          console.log('[Legal Name Fix] ✅ Revalidation PASSED - Showing success and emitting completed');
          // Revalidação passou! Mostrar tela de sucesso
          this.hasProcessingError = false;
          this.currentError = null;
          this.processing = ProcessingStatus.APPROVED;
          this.steps = 'SUCCESS';
          this.result = {
            status: 'VERIFIED',
            checkId: this.checkId,
            message: result.message || this.translate.instant('identityVerified'),
          };

          // REMOVED: Do not emit completed automatically so the user can see the success screen.
          // The user will click "Proceed" which calls close() and emits the event.
          // this.completed.emit({
          //   status: 'VERIFIED',
          //   checkId: this.checkId,
          //   fromLegalNameFix: true,
          // });
        } else if (result?.revalidated && result?.passed === false) {
          console.log('[Legal Name Fix] ❌ Revalidation FAILED - Back to upload');
          // Revalidação falhou, voltar para upload
          this.hasProcessingError = false;
          this.currentError = null;

          // Se tiver arquivos, tenta processar novamente automaticamente
          if (this.frontImageFile) {
            console.log('[Legal Name Fix] Files exist, retrying submission automatically');
            this.steps = 'UPLOAD';
            this.processUpload();
          } else {
            this.steps = 'UPLOAD';
          }
        } else {
          console.log('[Legal Name Fix] Data updated, restarting KYC process');
          // Dados foram atualizados, reiniciar processo do zero
          this.hasProcessingError = false;
          this.currentError = null;
          this.steps = 'TYPE-DOC';
          this.clearAllFiles();
        }
      } else if (result?.resubmitDocuments) {
        console.log('[Legal Name Fix] User needs to resubmit documents');
        // Voltar para upload com contador de tentativas atualizado
        this.hasProcessingError = false;
        this.currentError = null;

        // Recarregar retry count para garantir que está atualizado
        // (isso conta como uma nova tentativa)
        this.loadUserRetryCount();

        // Limpar arquivos antigos para forçar novo upload
        this.clearAllFiles();

        this.steps = 'UPLOAD';
      } else {
        console.log('[Legal Name Fix] User cancelled, keeping error card visible');
        // Usuário cancelou, mantém o card de erro visível no step PROCESSING
        // Não muda para ERROR screen, mantém hasProcessingError = true
      }
    });
  }
}

// ── KYC outcome decoding ───────────────────────────────────────────────────────
/**
 * Decode the terminal `provider_return` + `notes` of a check into a compact, human-readable set of
 * audit fields for the `result` trace event: final verification status/reason, SERPRO/registry
 * outcome, PEP/watchlist hits, decline codes, resubmission, and the postpone reason. Defensive
 * against the loose jsonb shape — every field is optional and missing ones are dropped.
 */
function decodeKycOutcome(notes?: any[], providerReturn?: any): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const pr = providerReturn && typeof providerReturn === 'object' ? providerReturn : null;

  if (pr) {
    out['passed'] = pr.passed ?? null;
    out['finalStage'] = pr.stage ?? null;

    const v = pr.verification && typeof pr.verification === 'object' ? pr.verification : null;
    if (v) {
      out['verificationStatus'] = v.status ?? null;
      out['verificationReason'] = v.reason ?? null;
      out['verificationReasonCode'] = v.reasonCode ?? v.code ?? null;
      if (v.person?.pepSanctionMatch != null) out['pepSanctionMatch'] = v.person.pepSanctionMatch;
    }

    const registryStatus = pr.registry_status ?? pr.registry_validation?.status ?? null;
    if (registryStatus) out['registryStatus'] = registryStatus;
    if (pr.serpro_validation?.name) out['serproName'] = pr.serpro_validation.name;

    if (pr.awaiting_name_confirmation) {
      out['awaitingNameConfirmation'] = {
        currentName: pr.awaiting_name_confirmation.currentName ?? null,
        suggestedName: pr.awaiting_name_confirmation.suggestedName ?? null,
      };
    }

    if (pr.rejection_reason) out['rejectionReason'] = pr.rejection_reason;

    const pep = pr.pep_sanctions_screening;
    if (pep && typeof pep === 'object') {
      out['pepHits'] = Array.isArray(pep.hits) ? pep.hits.length : null;
      if (pep.status) out['pepStatus'] = pep.status;
    }
  }

  // Decline / resubmission signals live in the notes' `data` envelopes.
  if (Array.isArray(notes) && notes.length) {
    const datas = notes.map((n) => n?.data).filter((d) => d && typeof d === 'object');
    const declineCodes = datas
      .map((d: any) => d.declinedReason?.reasonCode ?? (d.status === 'declined' ? d.errorCode : null))
      .filter((c: unknown) => c != null);
    if (declineCodes.length) out['declineCodes'] = [...new Set(declineCodes)];
    if (datas.some((d: any) => d.isBlocked === true)) out['blocked'] = true;
    if (datas.some((d: any) => d.status === 'resubmission_requested')) out['resubmission'] = true;

    const postpone = datas.find((d: any) => d.postpone_reason)?.postpone_reason;
    if (postpone) {
      out['postponeReason'] = {
        canProceed: postpone.canProceed ?? null,
        code: postpone.code ?? postpone.reason ?? null,
      };
    }
  }

  return out;
}

// ── brdocs → trace mapping ─────────────────────────────────────────────────────
/** brdocs debug category → timeline phase. */
const BRDOCS_CATEGORY_PHASE: Record<string, KycTracePhase> = {
  camera: 'capture',
  pipeline: 'capture',
  model: 'classify',
  classify: 'classify',
  ocr: 'ocr',
  qr: 'qr',
};

/** brdocs user-action → timeline phase. */
const BRDOCS_ACTION_PHASE: Record<string, KycTracePhase> = {
  'started': 'capture',
  'method-selected': 'capture',
  'front-captured': 'classify',
  'front-accepted': 'capture',
  'front-retaken': 'capture',
  'back-captured': 'classify',
  'back-accepted': 'capture',
  'back-retaken': 'capture',
  'qr-read': 'qr',
  'qr-skipped': 'qr',
  'not-document': 'classify',
  'not-document-limit': 'classify',
  'not-document-overridden': 'classify',
  'not-document-blocked': 'classify',
  'navigated-back': 'capture',
  'voice-toggled': 'capture',
  'completed': 'capture',
  'restarted': 'capture',
};

/** Human one-liner for a brdocs action, surfacing the key detail (method / doc type / QR kind). */
function BRDOCS_ACTION_TITLE(
  action: string,
  method?: string,
  detail?: Record<string, unknown>,
): string {
  switch (action) {
    case 'method-selected':
      return `Capture method: ${method === 'upload' ? 'upload from device' : 'live photo'}`;
    case 'front-captured':
      return `Front captured — classified as "${detail?.['type'] ?? 'unknown'}"${detail?.['recognized'] === false ? ' (unrecognised)' : ''}${detail?.['digital'] ? ' · digital' : ''}`;
    case 'back-captured':
      return `Back captured — "${detail?.['type'] ?? 'unknown'}"`;
    case 'front-accepted':
      return 'User accepted the front image';
    case 'back-accepted':
      return 'User accepted the back image';
    case 'front-retaken':
      return 'User retook the front';
    case 'back-retaken':
      return 'User retook the back';
    case 'qr-read':
      return `QR / MRZ decoded — ${detail?.['kind'] ?? 'document'}`;
    case 'qr-skipped':
      return 'QR skipped (could not read)';
    case 'not-document':
      return `Not a document (${detail?.['side'] ?? 'front'}) — asked to send another image`;
    case 'not-document-limit':
      return 'Not a document too many times in a row — capture restarted from the first screen';
    case 'not-document-overridden':
      return 'Not a document too many times in a row — let through (safety valve set to proceed)';
    case 'not-document-blocked':
      return 'Too many images that are not a document — new images blocked for a while (anti-spam)';
    case 'started':
      return 'Capture flow started';
    case 'restarted':
      return 'Capture restarted (another document)';
    case 'voice-toggled':
      return `Voice guidance ${detail?.['on'] ? 'on' : 'off'}`;
    case 'navigated-back':
      return 'Stepped back a screen';
    case 'completed':
      return 'Capture compiled a result';
    default:
      return action;
  }
}
